import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Card,
  Table,
  InputNumber,
  Button,
  Typography,
  Tabs,
  Space,
  message,
  Select,
  Tooltip,
  Checkbox,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DownloadOutlined,
  ArrowRightOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import DeleteConfirmationModal from "../../common/DeleteConfirmationModal";
import {
  ColumnConfigModal,
  selectStyles,
} from "../../common/ColumnConfigModal";
import { useTableRenderDeferral } from "../../../hooks/useTableRenderDeferral";
import TableLoadingPlaceholder from "../../common/TableLoadingPlaceholder";
import { useTender } from "../../../contexts/TenderContext";
import { useAuth } from "../../../hooks/useAuth";
import { useScrollToTop } from "../../../hooks/useScrollToTop";
import MainLayout from "../../layout/MainLayout";
import { useExcelImportExport } from "../../../hooks/useExcelImportExport";
import { ExcelImportExport } from "../../common/ExcelImportExport";
import { ExcelColumn } from "../../../utils/excelUtils";
import { COLORS, EVALUATE_CODES, EvaluateCode } from "../../../utils/constants";
import { useNavigate } from "react-router-dom";
import { prepareTenderForAPI } from "../../../utils/storage";
import { useCreateBillOfQuantities } from "../../../hooks/useBillOfQuantities";
import NumberInput from "../../common/NumberInput";
import BufferedTextInput from "../../common/BufferedTextInput";
// import { useChangeDetection } from "../../../hooks/useChangeDetection";
import { useGroundworksTotalCalculations } from "../../../hooks/useGroundworksTotalCalculations";
import { forceSaveBothBoqEndpoints } from "../../../utils/boqVersionUtils";
import { useItemLibrary } from "../../../hooks/useItemLibrary";
import { addColumnCodes } from "@/utils/addColumnCodes";
import { FOUNDATIONS_REFERENCES, CELCON_GENERAL_CODE_OVERRIDES, CELCON_BLOCK_TYPES, CONCRETE_STRIP_CODE_MAP, CONCRETE_FIELD_PREFIX_CODES } from "@/utils/foundationsReferenceMapping";
import { isDuplicatePlotNo } from "@/utils/plotNoValidation";
import { normaliseDecimalMmInLabel, formatDimensionLabel } from "../../../utils/dimensionLabel";

const { Title, Text } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

const MESH_REINFORCEMENT_CODE_MAP: Record<string, string> = {
  'A142':  'GWALPRM001',
  'A193':  'GWALPRM002',
  'A252':  'GWALPRM003',
  'A393':  'GWALPRM004',
  'B283':  'GWALPRM017',
  'B385':  'GWALPRM005',
  'B503':  'GWALPRM006',
  'B785':  'GWALPRM007',
  'B1131': 'GWALPRM008',
};


const excelColumns: ExcelColumn[] = [
  { key: "plotNo", title: "Plot No" },
  { key: "housetype", title: "Housetype" },
  { key: "foundationType", title: "Foundation Type" },
  // Excavate & Concrete fields
  { key: "pileMat", title: "xxxmm thick recycled 6F2 piling mat" },
  { key: "pileMatRemoval", title: "Excavate to remove pile mat & re-use" },
  { key: "plotGarSetOut", title: "Engineer setting out houses & garages" },
  { key: "pileSetOut", title: "Setting out for piling contractor" },
  { key: "pileAttendance", title: "Attendance upon piling contractor" },
  { key: "excavateTrench", title: "Excavate trench and tip on site" },
  { key: "excavatePiled", title: "Excavate trench piled foundation and tip on site" },
  { key: "excavateVibro", title: "Excavate vibro foundation and tip on site" },
  { key: "levelCompact", title: "Consolidate bottoms of trenches" },
  { key: "stockpileArisings", title: "Deposit on site (arisings)" },
  { key: "concreteBlinding", title: "xxmm thick concrete blinding" },
  { key: "formplasOrSimilar", title: "Formwork to groundbeams (Formplas or similar)" },
  { key: "cropPiles", title: "Pile crop" },
  { key: "fillSteelPiles", title: "Filling Steel Piles" },
  { key: "rebar", title: "Steel Reinforcement (bar)" },
  { key: "stepsInFounds", title: "EO step at party wall" },
  { key: "meshReinforcement", title: "Mesh Reinforcement" },
  { key: "gen1ConcreteToStrip", title: "GEN 1 Concrete in foundation" },
  { key: "rc35ConcreteToPiled", title: "RC35 Concrete to Piled foundation" },
  { key: "rc35ConcreteToVibro", title: "RC35 Concrete to Vibro foundation" },
  { key: "stripClaymaster", title: "Strip Claymaster" },
  { key: "piledClaymaster", title: "Piled Claymaster" },
  // Substructure fields
  { key: "trench4NBlock300mmWide", title: "Trench block 300mm wide 3.6N - Thermalite" },
  { key: "trench7NBlock300mmWide", title: "Trench block 300mm wide 7.0N - Thermalite" },
  { key: "trench4NBlock355mmWide", title: "Trench block 355mm wide 3.6N - Thermalite" },
  { key: "trench7NBlock355mmWide", title: "Trench block 355mm wide 7.0N - Thermalite" },
  { key: "dense4NBlockwork100mm", title: "3.6N 100mm Dense Blockwork" },
  { key: "dense7NBlockwork100mm", title: "Dense concrete blockwork 100 thk 7n/mm2 (Ext Leaf)" },
  { key: "dense10NBlockwork100mm", title: "10N 100mm Dense Blockwork" },
  { key: "aerated4NBlockwork100mm", title: "Aerated concrete blockwork 100 thk 3.6n/mm2 (Ext leaf) - Thermalite" },
  { key: "aerated7NBlockwork100mm", title: "Aerated concrete blockwork 100 thk 7n/mm2 (Ext leaf) - Thermalite" },
  { key: "aerated4NBlockwork140mm", title: "Aerated concrete blockwork 140 thk 3.6n/mm2 - Thermalite" },
  { key: "aerated7NBlockwork140mm", title: "Aerated concrete blockwork 140 thk 7n/mm2 - Thermalite" },
  { key: "dense100mmCoursingBrick", title: "Dense HB Common Brickwork - 100mm" },
  { key: "aerated100mmCoursingBrick", title: "Aerated HB Common Brickwork - 100mm - Thermalite" },
  { key: "aerated140mmCoursingBrick", title: "Aerated HB Common Brickwork - 140mm - Thermalite" },
  { key: "layFacingBrickwork", title: "HB wall in facings FREE ISSUE - 215 x 102.5 x 65mm" },
  { key: "supplyFacingBrickwork", title: "HB wall in Red Class B's (perf) M&J Supply - 215 x 102.5 x 65mm" },
  { key: "beamInfill", title: "T Closure (dense 100 wide)" },
  { key: "formCavity", title: "Form Cavity" },
  { key: "cavityFill", title: "Cavity infill concrete" },
  { key: "telescopicVents", title: "Telescopic vents (including lintel) - 100 wide" },
  { key: "telescopicVents140", title: "Telescopic vents (including lintel) - 140 wide" },
  { key: "perimeterInsulation", title: "25mm Perimeter Strip" },
  { key: "perimeterInsulation30", title: "30mm Perimeter Strip" },
  { key: "dpc100mm", title: "DPC 100mm wide" },
  { key: "backfillExcavations", title: "Backfill with as dug" },
  { key: "cavityInsulation", title: "Cavity insulation" },
  // Abnormals fields
  { key: "additionalExcavation", title: "Additional Excavation" },
  { key: "gen1AdditionalConcrete", title: "in additional concrete" },
  { key: "additional300mmTrenchBlock", title: "Additional 300mm Trench Block" },
  { key: "additional355mmTrenchBlock", title: "Additional 355mm Trench Block" },
  { key: "additional100mmBlockwork", title: "Additional 7.2N Aerated 100mm Blockwork" },
  {
    key: "backfillExcavationsAsDug",
    title: "Backfill excavations with as dug",
  },
  { key: "depositOnSite", title: "Deposit on site" },
  { key: "eoExposedBrickwork", title: "EO exposed brickwork" },
  { key: "eoTanking", title: "EO tanking, inc retaining blockwork" },
  { key: "depositCFAArisings", title: "Deposit CFA arisings on site" },
  { key: "pileCollar", title: "Pile Collar" },
  { key: "cordekCellcore155", title: "Cordek Cellcore 155mm HX-B" },
  { key: "cordekCellcore225", title: "Cordek Cellcore 225mm HX-B" },
  // Floors fields
  { key: "bbFloorHouse", title: "B&B floor House" },
  { key: "bbFloorGarage", title: "B&B floor Garage" },
  { key: "layOnlyFloors", title: "Lay only Floors" },
  { key: "insituFloorHouse", title: "Insitu Floor House" },
  { key: "topHats", title: "Top Hats" },
  { key: "eo2000gDPM", title: "EO 2000g DPM" },
  { key: "eoAmber1DPM", title: "EO Amber 1 DPM" },
  { key: "eoAmber2DPM", title: "EO AMBER 2 DPM" },
  { key: "radonMembrane", title: "Radon Membrane" },
  { key: "gasMembrane", title: "Gas Membrane with validation" },
  { key: "meshToFloor", title: "Mesh to floor" },
  { key: "topLayerMesh", title: "Top layer Mesh" },
  { key: "insituFloorGarage", title: "Cast Insitu Slab - Garages" },
  { key: "meshToGarageFloor", title: "Mesh to Garage floor" },
];

interface FoundationRow {
  key: string;
  plotNo: string;
  housetype: string;
  foundationType: string;
  plotType: "plot" | "garage";
  pileMat: number;
  pileMatRemoval: number;
  plotGarSetOut: number;
  pileSetOut: number;
  pileAttendance: number;
  excavateTrench: number;
  excavatePiled: number;
  excavateVibro: number;
  levelCompact: number;
  stockpileArisings: number;
  concreteBlinding: number;
  formplasOrSimilar: number;
  cropPiles: number;
  fillSteelPiles: number;
  rebar: number;
  stepsInFounds: number;
  meshReinforcement: number;
  gen1ConcreteToStrip: number;
  rc35ConcreteToPiled: number;
  rc35ConcreteToVibro: number;
  stripClaymaster: number;
  piledClaymaster: number;
  // Substructure fields
  trench4NBlock300mmWide: number;
  trench7NBlock300mmWide: number;
  trench4NBlock355mmWide: number;
  trench7NBlock355mmWide: number;
  dense4NBlockwork100mm: number;
  dense7NBlockwork100mm: number;
  dense10NBlockwork100mm: number;
  aerated4NBlockwork100mm: number;
  aerated7NBlockwork100mm: number;
  aerated4NBlockwork140mm: number;
  aerated7NBlockwork140mm: number;
  dense100mmCoursingBrick: number;
  aerated100mmCoursingBrick: number;
  aerated140mmCoursingBrick: number;
  layFacingBrickwork: number;
  supplyFacingBrickwork: number;
  beamInfill: number;
  formCavity: number;
  cavityFill: number;
  telescopicVents: number;
  telescopicVents140: number;
  perimeterInsulation: number;
  perimeterInsulation30: number;
  dpc100mm: number;
  backfillExcavations: number;
  cavityInsulation: number;
  // Abnormals fields
  additionalExcavation: number;
  gen1AdditionalConcrete: number;
  additional300mmTrenchBlock: number;
  additional355mmTrenchBlock: number;
  additional100mmBlockwork: number;
  backfillExcavationsAsDug: number;
  depositOnSite: number;
  eoExposedBrickwork: number;
  eoTanking: number;
  depositCFAArisings: number;
  pileCollar: number;
  cordekCellcore155: number;
  cordekCellcore225: number;
  // Floors fields
  bbFloorHouse: number;
  bbFloorGarage: number;
  layOnlyFloors: number;
  insituFloorHouse: number;
  topHats: number;
  eo2000gDPM: number;
  eoAmber1DPM: number;
  eoAmber2DPM: number;
  radonMembrane: number;
  gasMembrane: number;
  meshToFloor: number;
  topLayerMesh: number;
  insituFloorGarage: number;
  meshToGarageFloor: number;
}

interface FoundationsTableViewProps {
  isLoading: boolean;
  dataSource: FoundationRow[];
  columns: any[];
  rowSelection: NonNullable<React.ComponentProps<typeof Table>["rowSelection"]>;
  scroll: { x: number; y?: number };
  sticky: { offsetHeader: number };
  renderSummary: () => React.ReactNode;
}

// Wraps each of the four Foundations tables (Excavate & Concrete,
// Substructure, Abnormals, Floors) so React.memo can bail out of
// reconciling one entirely when only `activeTab` changes and none of its
// OWN props did - which is what makes switching back to an already-mounted
// tab feel instant (mirrors CutFills.tsx's CutFillsTableView). Without this
// memoization, keeping all four tables mounted alone doesn't help - the
// parent component re-rendering on every `activeTab` change would still
// reconcile every table's full row/cell tree even while three of them are
// `display: none`.
const FoundationsTableView = React.memo(function FoundationsTableView({
  isLoading,
  dataSource,
  columns,
  rowSelection,
  scroll,
  sticky,
  renderSummary,
}: FoundationsTableViewProps) {
  if (isLoading) return <TableLoadingPlaceholder />;
  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      rowSelection={rowSelection}
      scroll={scroll}
      sticky={sticky}
      size="middle"
      bordered
      className="foundations-table"
      summary={renderSummary}
    />
  );
});

const FoundationsNew: React.FC = () => {
  // Scroll to top on page load
  useScrollToTop();

  const { state: tender, updateField, saveTender } = useTender();
  const { user } = useAuth();
  const navigate = useNavigate();
  const createBoqMutation = useCreateBillOfQuantities();

  /* const {
    hasChanges,
    trackFieldChange,
    trackRowAdded,
    trackRowDeleted,
    resetChanges,
    getChangeSummary,
  } = useChangeDetection<FoundationRow[]>({
    pageId: "foundations-new",
    onChangeDetected: (changes) => {
    },
  }); */

  // Checkbox state logic for adjustments
  const getCheckboxState = (key: string, fallbackPercent: number): boolean => {
    const saved = localStorage.getItem(key);
    if (saved !== null) {
      return JSON.parse(saved);
    }
    return fallbackPercent > 0;
  };

  const preliminariesPercent = tender.globalSettings?.groundworks?.preliminaries || 0;
  const ohpPercent = tender.globalSettings?.groundworks?.overheadAndProfit || 0;
  const discountPercent = tender.globalSettings?.groundworks?.discount || 0;

  const includePreliminaries = getCheckboxState("groundworks_includePreliminariesDisplay", preliminariesPercent);
  const includeOhp = getCheckboxState("groundworks_includeOhpDisplay", ohpPercent);
  const includeDiscount = getCheckboxState("groundworks_includeDiscountDisplay", discountPercent);

  // Use the EXACT same hook as GroundworksReview for calculations
  const groundworksTotals = useGroundworksTotalCalculations({
    includePreliminaries,
    includeOhp,
    includeDiscount,
  });
  const { data: itemLibraryData } = useItemLibrary(tender.region || "");

  const [data, setData] = useState<FoundationRow[]>([]);
  const [plotTypeFilter, setPlotTypeFilter] = useState<"plots" | "garages">("plots");
  const [activeTab, setActiveTab] = useState<string>("excavate-concrete");

  // Excavate & Concrete / Substructure / Abnormals / Floors previously fully
  // unmounted/remounted their <Table> on every tab switch (`{activeTab ===
  // "x" && <Table .../>}`), re-paying AntD's full Table mount cost every
  // time - the actual cause of tab switching feeling slow (same root cause
  // fixed in CutFills.tsx).
  //
  // All four tables are mounted UPFRONT (not lazily on first visit) -
  // `mountedTabs` starts with all four keys already in it and nothing ever
  // adds to it later - so the one-time mount cost for all four is paid once
  // on page load, and every tab switch after that (including the very first
  // one) is a pure CSS `display` toggle with zero React/AntD mount work
  // (same eager-preload approach applied to CutFills.tsx).
  const [mountedTabs] = useState<Set<string>>(
    () => new Set(["excavate-concrete", "substructure", "abnormals", "floors"]),
  );

  // One `useTableRenderDeferral` per table (all four fire once, together, on
  // page load, covering each table's real one-time mount) so a tender with a
  // lot of plots doesn't make the initial page load look frozen while all
  // four tables' first layout blocks the main thread. Once they flip false
  // (moments after mount) they never fire again, since `mountedTabs` is fixed
  // for the component's lifetime.
  const isExcavateConcreteTableLoading = useTableRenderDeferral([
    mountedTabs.has("excavate-concrete"),
  ]);
  const isSubstructureTableLoading = useTableRenderDeferral([
    mountedTabs.has("substructure"),
  ]);
  const isAbnormalsTableLoading = useTableRenderDeferral([
    mountedTabs.has("abnormals"),
  ]);
  const isFloorsTableLoading = useTableRenderDeferral([
    mountedTabs.has("floors"),
  ]);
  const [stickyOffset, setStickyOffset] = useState<number>(144);
  // Memoized: all four tabs share the same header layout (Configure Headers
  // button/View filter aren't conditional on activeTab here, unlike
  // CutFills.tsx), so one shared value is correct - but it must still be
  // memoized rather than passed as a fresh `{ offsetHeader: stickyOffset }}`
  // literal in JSX, otherwise every render would hand FoundationsTableView's
  // React.memo a "changed" prop even when stickyOffset itself didn't change,
  // defeating the tab-switch memoization below.
  const stickyConfig = React.useMemo(
    () => ({ offsetHeader: stickyOffset }),
    [stickyOffset],
  );
  const [deleteModalVisible, setDeleteModalVisible] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState<string | null>(null);
  const [keysToDelete, setKeysToDelete] = useState<string[]>([]);
  const [selectedFoundationKeys, setSelectedFoundationKeys] = useState<React.Key[]>([]);

  // rowSelection is intentionally the SAME object for all four tables
  // (selection is genuinely shared state across this page's tabs), memoized
  // so its identity is stable across renders that don't touch selection
  // (e.g. a plain tab switch) - required for FoundationsTableView's
  // React.memo below to actually skip re-rendering a hidden table.
  const rowSelectionConfig = React.useMemo(
    () => ({
      selectedRowKeys: selectedFoundationKeys,
      onChange: setSelectedFoundationKeys,
    }),
    [selectedFoundationKeys],
  );

  // Mesh reinforcement type state
  const [meshReinforcementType, setMeshReinforcementType] = useState<string>(
    tender.groundworks?.meshReinforcementType ?? ""
  );

  // Substructure block type state
  const [substructureBlockType, setSubstructureBlockType] = useState<string>(
    (tender.groundworks as any)?.substructureBlockType ?? ""
  );

  // Prefix values state for dynamic concrete type fields
  const [prefixValues, setPrefixValues] = useState<Record<string, string>>({});

  // Merge static references with any evaluate codes stored in prefixValues / meshReinforcementType
  const effectiveFoundationsRefs = React.useMemo(() => {
    const merged = { ...FOUNDATIONS_REFERENCES };
    // Apply Celcon block type code overrides (same codes for both Celcon variants)
    if (substructureBlockType && (CELCON_BLOCK_TYPES as readonly string[]).includes(substructureBlockType)) {
      Object.entries(CELCON_GENERAL_CODE_OVERRIDES).forEach(([field, code]) => {
        merged[field] = { code };
      });
    }
    // insituFloorHouse stays static; bbFloorGarage is now dynamic (Configure modal
    // Floors → Garage lets the user pick GWALPF006 / GWALPF012) so it must NOT be
    // excluded from the prefixValues override loop.
    const staticFields = new Set(['insituFloorHouse']);
    Object.entries(prefixValues).forEach(([key, val]) => {
      if (!staticFields.has(key) && /^(ALP|GWALP|ACL|ZEV|HAUL)[A-Z0-9]/i.test(val)) {
        merged[key] = { code: val.toUpperCase() };
      }
    });
    // Concrete type fields: map selected prefix (e.g. "GEN1", "RC28/35") to its RMC
    // code, then combine with the field's placement prefix (CONCRETE_FIELD_PREFIX_CODES)
    // so downstream rate lookup sums the placement rate (GWALP013 / GWALP019a / GWALP025)
    // with the selected ready-mix concrete (RMC<xxx>) rate. `gen1AdditionalConcrete`
    // (Abnormals tab) shares the GWALP013 prefix used by `gen1ConcreteToStrip`.
    // When no concrete type is selected the placement-only code is used as a fallback
    // so the placement rate is always visible in the column header badge.
    for (const field of ['gen1ConcreteToStrip', 'rc35ConcreteToPiled', 'rc35ConcreteToVibro', 'gen1AdditionalConcrete'] as const) {
      const rmc = prefixValues[field] ? CONCRETE_STRIP_CODE_MAP[prefixValues[field]] : undefined;
      const prefix = CONCRETE_FIELD_PREFIX_CODES[field];
      if (rmc) {
        merged[field] = { code: prefix ? `${prefix}+${rmc}` : rmc };
      } else if (prefix) {
        // No concrete type selected — fall back to placement code so the rate is still shown
        merged[field] = { code: prefix };
      }
    }
    // Mesh reinforcement: map selected type (e.g. "A393") to its GWALPRM code
    const meshCode = meshReinforcementType ? MESH_REINFORCEMENT_CODE_MAP[meshReinforcementType] : undefined;
    if (meshCode) merged['meshReinforcement'] = { code: meshCode };
    // Mesh to floor: same code map, value stored in prefixValues
    const meshToFloorCode = prefixValues['meshToFloor'] ? MESH_REINFORCEMENT_CODE_MAP[prefixValues['meshToFloor']] : undefined;
    if (meshToFloorCode) merged['meshToFloor'] = { code: meshToFloorCode };
    // Top layer mesh: same code map, value stored in prefixValues
    const topLayerMeshCode = prefixValues['topLayerMesh'] ? MESH_REINFORCEMENT_CODE_MAP[prefixValues['topLayerMesh']] : undefined;
    if (topLayerMeshCode) merged['topLayerMesh'] = { code: topLayerMeshCode };
    // Mesh to garage floor: same code map, value stored in prefixValues
    const meshToGarageFloorCode = prefixValues['meshToGarageFloor'] ? MESH_REINFORCEMENT_CODE_MAP[prefixValues['meshToGarageFloor']] : undefined;
    if (meshToGarageFloorCode) merged['meshToGarageFloor'] = { code: meshToGarageFloorCode };
    // Terram on Piling Mat: when enabled, the pileMat resource code becomes
    // the combined "ALP031A+GWALP004" so downstream rate lookup sums both nettRates.
    if (prefixValues['terram'] === 'true') {
      merged['pileMat'] = { code: 'ALP031A+GWALP004' };
    }
    return merged;
  }, [prefixValues, meshReinforcementType, substructureBlockType]);

  // Temporary state for pending changes (only applied when user clicks Apply)
  const [tempMeshReinforcementType, setTempMeshReinforcementType] =
    useState<string>("");
  const [tempSubstructureBlockType, setTempSubstructureBlockType] =
    useState<string>("");
  const [tempPrefixValues, setTempPrefixValues] = useState<
    Record<string, string>
  >({});

  // Modal visibility state
  const [configModalVisible, setConfigModalVisible] = useState(false);

  // Track previous configModalVisible state to detect when modal opens
  const prevConfigModalVisibleRef = useRef(configModalVisible);

  // Reset temp values when modal opens (configModalVisible goes from false to true)
  useEffect(() => {
    if (configModalVisible && !prevConfigModalVisibleRef.current) {
      // Modal just opened, reset temp values to current values
      setTempMeshReinforcementType(meshReinforcementType);
      setTempSubstructureBlockType(substructureBlockType);
      setTempPrefixValues({ ...prefixValues });
    }
    prevConfigModalVisibleRef.current = configModalVisible;
  }, [configModalVisible, meshReinforcementType, substructureBlockType, prefixValues]);

  // Save mesh reinforcement type to context
  useEffect(() => {
    if (meshReinforcementType !== undefined && meshReinforcementType !== tender.groundworks?.meshReinforcementType) {
      updateField("groundworks", {
        ...tender.groundworks,
        meshReinforcementType,
      });
    }
  }, [meshReinforcementType, updateField, tender.groundworks]);

  // Save substructure block type to context
  useEffect(() => {
    if (substructureBlockType !== undefined && substructureBlockType !== (tender.groundworks as any)?.substructureBlockType) {
      updateField("groundworks", {
        ...tender.groundworks,
        substructureBlockType,
      });
    }
  }, [substructureBlockType, updateField, tender.groundworks]);

  // Load prefix values from context (from bulk import)
  useEffect(() => {
    const contextPrefixValues = (tender.groundworks as any)
      ?.foundationsPrefixValues;
    if (contextPrefixValues && Object.keys(contextPrefixValues).length > 0) {
      // Only update if values are different to prevent infinite loop
      const isDifferent =
        JSON.stringify(contextPrefixValues) !== JSON.stringify(prefixValues);
      if (isDifferent) {
        setPrefixValues(contextPrefixValues);
      }
    }
  }, [tender.groundworks]);

  // Save prefix values to context when they change.
  // Skip when local state already matches context (initial load sync) to avoid false isDirty.
  useEffect(() => {
    if (Object.keys(prefixValues).length === 0) return;
    const contextValues = (tender.groundworks as any)?.foundationsPrefixValues;
    if (JSON.stringify(contextValues ?? {}) === JSON.stringify(prefixValues)) return;
    updateField("groundworks", {
      ...tender.groundworks,
      foundationsPrefixValues: prefixValues,
    } as any);
  }, [prefixValues, tender.groundworks, updateField]);

  // Recalculate sticky offset when tab changes
  useEffect(() => {
    const calculateOffset = () => {
      const baseOffset = 144; // MainLayout header offset
      const stickyHeader = document.getElementsByClassName(
        "foundations-sticky-header"
      )?.[0];
      const stickyHeaderHeight =
        stickyHeader?.getBoundingClientRect?.()?.height || 0;
      setStickyOffset(baseOffset + stickyHeaderHeight);
    };

    // Calculate immediately
    calculateOffset();

    // Recalculate after a short delay to ensure DOM has updated
    const timer = setTimeout(calculateOffset, 100);

    return () => clearTimeout(timer);
  }, [activeTab]);

  // Helper function to get column title with replaced prefix
  const getColumnTitle = React.useCallback(
    (fieldKey: string, defaultTitle: string): string => {
      // Return the constructed title as-is so decimal-mm prefixes from Excel
      // (e.g. "0.150mm") are preserved exactly as imported.
      if (prefixValues[fieldKey] && prefixValues[fieldKey].trim() !== "") {
        return defaultTitle.replace(/xx/g, prefixValues[fieldKey]);
      }
      return defaultTitle;
    },
    [prefixValues]
  );

  // Generic helper function to format column titles with field name and unit
  const formatColumnTitle = React.useCallback(
    (baseTitle: string, appendText: string = ""): React.ReactNode => {
      // Extract unit from the end (e.g., "(m³)", "(m²)", "(m)", "(Nr)", "(t)" or "m³", "m²", "m", "Nr", "t")
      const unitMatch = baseTitle.match(/\s*\((m[²³]|m|Nr|t|m2|m3)\)$|\s+(m[²³]|m|Nr|t|m2|m3)$/);
      let titleWithoutUnit = baseTitle;
      let unit = "";

      if (unitMatch) {
        unit = unitMatch[1] || unitMatch[2];
        titleWithoutUnit = baseTitle.replace(/\s*\((m[²³]|m|Nr|t|m2|m3)\)$|\s+(m[²³]|m|Nr|t|m2|m3)$/, "");
      }

      const fullTitle = appendText && appendText.trim() !== ""
        ? `${titleWithoutUnit} ${appendText}`
        : titleWithoutUnit;

      if (appendText && appendText.trim() !== "") {
        return (
          <div className="text-center">
            <Tooltip title={fullTitle}>
              <div className="gw-header-title">{fullTitle}</div>
            </Tooltip>
            {unit && (
              <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
            )}
          </div>
        );
      }

      if (unit) {
        return (
          <div className="text-center">
            <Tooltip title={titleWithoutUnit}>
              <div className="gw-header-title">{titleWithoutUnit}</div>
            </Tooltip>
            <div className="gw-header-unit text-xs text-gray-500">{unit}</div>
          </div>
        );
      }

      return (
        <Tooltip title={baseTitle}>
          <div className="gw-header-title">{baseTitle}</div>
        </Tooltip>
      );
    },
    []
  );

  // Helper function to append block type to substructure column titles
  const getSubstructureColumnTitle = React.useCallback(
    (baseTitle: string): React.ReactNode => {
      return formatColumnTitle(baseTitle, substructureBlockType);
    },
    [substructureBlockType, formatColumnTitle]
  );

  const {
    importLoading,
    importModalVisible,
    setImportModalVisible,
    handleDownloadTemplate,
    handleImportExcel: baseHandleImportExcel,
  } = useExcelImportExport<FoundationRow>({
    filename: "Foundations",
    sheetName: "Foundations",
    columns: excelColumns,
    data: data,
    onImport: (importedData) => {
      const processedData = importedData.map((row, index) => {
        const emptyRow = createEmptyRow(`imported_${Date.now()}_${index}`);
        const merged = { ...emptyRow, ...row };

        // Round all numeric fields to 2 decimals
        Object.keys(merged).forEach((key) => {
          if (typeof merged[key] === "number" && key !== "key") {
            merged[key] = Math.round(merged[key] * 1000) / 1000;
          }
        });

        return merged;
      });
      setData(processedData);
      updateField("groundworks", {
        ...tender.groundworks,
        foundationsTable: processedData,
      });
    },
    skipValidation: true,
  });

  // Custom import handler with direct Excel processing
  const handleImportExcel = async (file: File) => {
    setImportModalVisible(false);

    try {
      const XLSX = await import("xlsx");
      const reader = new FileReader();

      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target?.result as ArrayBuffer);
          const workbook = XLSX.read(data, { type: "array" });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];

          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            header: 1,
            raw: true,
            blankrows: false,
          }) as any[][];


          let headerRowIndex = -1;
          for (let i = 0; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (
              row.some(
                (cell) =>
                  String(cell).includes("Plot") && String(cell).includes("No")
              )
            ) {
              headerRowIndex = i;
              break;
            }
          }


          if (headerRowIndex === -1) {
            alert("Could not find header row with 'Plot No'");
            return;
          }

          const headers = jsonData[headerRowIndex];
          headers.forEach((h: any, i: number) => {
            if (h) console.log(`  Column ${i}: "${h}"`);
          });

          // Search header row and subsequent sub-header rows (up to 4 rows below)
          // Excel has multi-row headers: Row 3 = "Plot No", Row 4 = group headers, Row 5 = detailed column titles
          const headerRowsToSearch: any[][] = [];
          for (let r = 0; r <= 4 && headerRowIndex + r < jsonData.length; r++) {
            if (jsonData[headerRowIndex + r]) {
              headerRowsToSearch.push(jsonData[headerRowIndex + r]);
            }
          }

          // Extract prefix values from headers (text before "Concrete to Strip foundation", etc.)
          const extractedPrefixValues: Record<string, string> = {};

          // Map field keys to their base text patterns
          const prefixFields = [
            {
              fieldKey: "gen1ConcreteToStrip",
              baseText: "Concrete to Strip foundation",
            },
            {
              fieldKey: "rc35ConcreteToPiled",
              baseText: "Concrete to Piled foundation",
            },
            {
              fieldKey: "rc35ConcreteToVibro",
              baseText: "Concrete to Vibro foundation",
            },
          ];

          // Search through all header rows to find matching columns
          prefixFields.forEach(({ fieldKey, baseText }) => {
            let found = false;
            for (const headerRow of headerRowsToSearch) {
              if (found) break;
              for (
                let columnIndex = 0;
                columnIndex < headerRow.length;
                columnIndex++
              ) {
                const headerValue = String(
                  headerRow[columnIndex] || ""
                ).trim();

                // Check if this header contains the base text (case insensitive)
                if (headerValue.toLowerCase().includes(baseText.toLowerCase())) {
                  // Extract prefix (text before the base text)
                  const escapedBaseText = baseText.replace(
                    /[.*+?^${}()|[\]\\]/g,
                    "\\$&"
                  );
                  const prefixMatch = headerValue.match(
                    new RegExp(`^(.+?)\\s*${escapedBaseText}`, "i")
                  );

                  if (prefixMatch && prefixMatch[1]) {
                    let extractedPrefix = prefixMatch[1].trim();

                    // Normalize the extracted prefix to match dropdown options
                    const normalized = extractedPrefix
                      .toUpperCase()
                      .replace(/\s+/g, "");

                    // Map common variations to standard format
                    if (
                      normalized === "RC28" ||
                      normalized === "RC35" ||
                      normalized === "RC28/35"
                    ) {
                      extractedPrefix = "RC28/35";
                    } else if (
                      normalized === "RC32" ||
                      normalized === "RC40" ||
                      normalized === "RC32/40"
                    ) {
                      extractedPrefix = "RC32/40";
                    } else if (normalized.startsWith("GEN")) {
                      extractedPrefix = normalized;
                    } else if (normalized.startsWith("FND")) {
                      extractedPrefix = normalized;
                    }

                    // Only store if it matches a known concrete type option.
                    // Unrecognised values (e.g. "RC95") are discarded so the
                    // column header falls back to the "xx" placeholder.
                    if (CONCRETE_STRIP_CODE_MAP[extractedPrefix]) {
                      extractedPrefixValues[fieldKey] = extractedPrefix;
                    }
                    found = true;
                    break;
                  }
                }
              }
            }
          });

          // Extract piling mat dimension from headers (e.g., "150mm thick recycled 6F2 piling mat")
          for (const headerRow of headerRowsToSearch) {
            let found = false;
            for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
              const headerValue = String(headerRow[columnIndex] || "").trim();
              if (headerValue.toLowerCase().includes("piling mat")) {
                const numericMatch = headerValue.match(/^(\d+(?:\.\d+)?)/);
                if (numericMatch) {
                  extractedPrefixValues.pileMat = numericMatch[1];
                }
                found = true;
                break;
              }
            }
            if (found) break;
          }

          // Extract concrete blinding thickness from headers (e.g., "75mm thick concrete blinding")
          for (const headerRow of headerRowsToSearch) {
            let found = false;
            for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
              const headerValue = String(headerRow[columnIndex] || "").trim();
              if (headerValue.toLowerCase().includes("concrete blinding")) {
                const numericMatch = headerValue.match(/^(\d+(?:\.\d+)?)/);
                if (numericMatch) {
                  extractedPrefixValues.concreteBlinding = numericMatch[1];
                }
                found = true;
                break;
              }
            }
            if (found) break;
          }

          // Extract mesh reinforcement type from headers
          const supportedMeshTypes = ["A142", "A193", "A252", "A393", "B283", "B385", "B503", "B785", "B1131"];
          for (const headerRow of headerRowsToSearch) {
            let found = false;
            for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
              const headerValue = String(headerRow[columnIndex] || "").trim();
              if (headerValue.toLowerCase().includes("mesh")) {
                for (const meshType of supportedMeshTypes) {
                  if (headerValue.toLowerCase().includes(meshType.toLowerCase())) {
                    extractedPrefixValues.meshReinforcement = meshType;
                    found = true;
                    break;
                  }
                }
                if (found) break;
              }
            }
            if (found) break;
          }


          // Extract B&B floor House type from headers - match against GWALPF codes
          const bbFloorCodes: string[] = [
            "GWALPF005", "GWALPF007", "GWALPF008", "GWALPF009", "GWALPF010",
            "GWALPF014", "GWALPF015", "GWALPF017", "GWALPF018", "GWALPF019",
            "GWALPF020", "GWALPF021",
          ];
          for (const headerRow of headerRowsToSearch) {
            let found = false;
            for (let columnIndex = 0; columnIndex < headerRow.length; columnIndex++) {
              const headerValue = String(headerRow[columnIndex] || "").trim();
              const headerLower = headerValue.toLowerCase();
              if (headerLower.includes("b&b") || headerLower.includes("beam")) {
                // Try exact match first, then partial match
                for (const code of bbFloorCodes) {
                  const codeLabel = EVALUATE_CODES[code as EvaluateCode]?.label?.toLowerCase();
                  if (codeLabel && (codeLabel === headerLower || headerLower.includes(codeLabel) || codeLabel.includes(headerLower))) {
                    extractedPrefixValues.bbFloorHouse = code;
                    found = true;
                    break;
                  }
                }
                if (found) break;
              }
            }
            if (found) break;
          }

          // Set the extracted prefix values to state
          if (Object.keys(extractedPrefixValues).length > 0) {
            setPrefixValues(extractedPrefixValues);
          }

          const allDataRows = jsonData.slice(headerRowIndex + 1);

          // Smart detection for multiple potential skip rows
          let dataRowsToImport = allDataRows;
          let skipCount = 0;

          // Check first few rows to determine skip pattern
          for (let i = 0; i < Math.min(3, allDataRows.length); i++) {
            const row = allDataRows[i];
            if (row && row.length > 0) {
              const firstCellValue = String(row[0] || "")
                .toLowerCase()
                .trim();

              // Check if this row should be skipped (totals, notes, or empty)
              const shouldSkip =
                firstCellValue.includes("total") ||
                firstCellValue.includes("sum") ||
                firstCellValue.includes("note") ||
                firstCellValue === "" ||
                (!isNaN(Number(firstCellValue)) &&
                  firstCellValue !== "" &&
                  !firstCellValue.match(/[a-zA-Z]/));

              if (shouldSkip) {
                skipCount++;
              } else {
                break;
              }
            }
          }

          dataRowsToImport = allDataRows.slice(skipCount);

          // Detect "GARAGES" separator row to tag rows as plot or garage
          let isGarageSection = false;
          const allImportedRows: FoundationRow[] = [];
          const filteredRows = dataRowsToImport.filter(
            (row) =>
              row &&
              row.length > 0 &&
              row.some(
                (cell) => cell !== null && cell !== undefined && cell !== ""
              )
          );

          filteredRows.forEach((row, index) => {
            const firstCell = String(row[0] || "").trim().toUpperCase();
            if (firstCell === "GARAGES" || firstCell === "GARAGE") {
              isGarageSection = true;
              return; // Skip the separator row itself
            }
            allImportedRows.push({
              key: `imported_${Date.now()}_${index}`,
              plotNo: String(row[0] || ""),
              housetype: String(row[1] || ""),
              foundationType: String(row[2] || ""),
              plotType: isGarageSection ? "garage" : "plot",
              pileMat: Math.round((Number(row[3]) || 0) * 1000) / 1000,
              pileMatRemoval: Math.round((Number(row[4]) || 0) * 1000) / 1000,
              plotGarSetOut: Math.round((Number(row[5]) || 0) * 1000) / 1000,
              pileSetOut: Math.round((Number(row[6]) || 0) * 1000) / 1000,
              pileAttendance: Math.round((Number(row[7]) || 0) * 1000) / 1000,
              excavateTrench: Math.round((Number(row[8]) || 0) * 1000) / 1000,
              excavatePiled: Math.round((Number(row[9]) || 0) * 1000) / 1000,
              excavateVibro: Math.round((Number(row[10]) || 0) * 1000) / 1000,
              levelCompact: Math.round((Number(row[11]) || 0) * 1000) / 1000,
              stockpileArisings:
                Math.round((Number(row[12]) || 0) * 1000) / 1000,
              concreteBlinding:
                Math.round((Number(row[13]) || 0) * 1000) / 1000,
              formplasOrSimilar:
                Math.round((Number(row[14]) || 0) * 1000) / 1000,
              cropPiles: Math.round((Number(row[15]) || 0) * 1000) / 1000,
              fillSteelPiles: Math.round((Number(row[16]) || 0) * 1000) / 1000,
              rebar: Math.round((Number(row[17]) || 0) * 1000) / 1000,
              stepsInFounds: Math.round((Number(row[18]) || 0) * 1000) / 1000,
              meshReinforcement:
                Math.round((Number(row[19]) || 0) * 1000) / 1000,
              gen1ConcreteToStrip:
                Math.round((Number(row[20]) || 0) * 1000) / 1000,
              rc35ConcreteToPiled:
                Math.round((Number(row[21]) || 0) * 1000) / 1000,
              rc35ConcreteToVibro:
                Math.round((Number(row[22]) || 0) * 1000) / 1000,
              stripClaymaster: Math.round((Number(row[23]) || 0) * 1000) / 1000,
              piledClaymaster: Math.round((Number(row[24]) || 0) * 1000) / 1000,
              trench4NBlock300mmWide:
                Math.round((Number(row[25]) || 0) * 1000) / 1000,
              trench7NBlock300mmWide:
                Math.round((Number(row[26]) || 0) * 1000) / 1000,
              trench4NBlock355mmWide:
                Math.round((Number(row[27]) || 0) * 1000) / 1000,
              trench7NBlock355mmWide:
                Math.round((Number(row[28]) || 0) * 1000) / 1000,
              dense4NBlockwork100mm:
                Math.round((Number(row[29]) || 0) * 1000) / 1000,
              dense7NBlockwork100mm:
                Math.round((Number(row[30]) || 0) * 1000) / 1000,
              dense10NBlockwork100mm:
                Math.round((Number(row[31]) || 0) * 1000) / 1000,
              aerated4NBlockwork100mm:
                Math.round((Number(row[32]) || 0) * 1000) / 1000,
              aerated7NBlockwork100mm:
                Math.round((Number(row[33]) || 0) * 1000) / 1000,
              aerated4NBlockwork140mm:
                Math.round((Number(row[34]) || 0) * 1000) / 1000,
              aerated7NBlockwork140mm:
                Math.round((Number(row[35]) || 0) * 1000) / 1000,
              dense100mmCoursingBrick:
                Math.round((Number(row[36]) || 0) * 1000) / 1000,
              aerated100mmCoursingBrick:
                Math.round((Number(row[37]) || 0) * 1000) / 1000,
              aerated140mmCoursingBrick:
                Math.round((Number(row[38]) || 0) * 1000) / 1000,
              layFacingBrickwork:
                Math.round((Number(row[39]) || 0) * 1000) / 1000,
              supplyFacingBrickwork:
                Math.round((Number(row[40]) || 0) * 1000) / 1000,
              beamInfill: Math.round((Number(row[41]) || 0) * 1000) / 1000,
              formCavity: Math.round((Number(row[42]) || 0) * 1000) / 1000,
              cavityFill: Math.round((Number(row[43]) || 0) * 1000) / 1000,
              telescopicVents: Math.round((Number(row[44]) || 0) * 1000) / 1000,
              telescopicVents140:
                Math.round((Number(row[45]) || 0) * 1000) / 1000,
              perimeterInsulation:
                Math.round((Number(row[46]) || 0) * 1000) / 1000,
              perimeterInsulation30:
                Math.round((Number(row[47]) || 0) * 1000) / 1000,
              dpc100mm: Math.round((Number(row[48]) || 0) * 1000) / 1000,
              backfillExcavations:
                Math.round((Number(row[49]) || 0) * 1000) / 1000,
              cavityInsulation:
                Math.round((Number(row[50]) || 0) * 1000) / 1000,
              additionalExcavation:
                Math.round((Number(row[51]) || 0) * 1000) / 1000,
              gen1AdditionalConcrete:
                Math.round((Number(row[52]) || 0) * 1000) / 1000,
              additional300mmTrenchBlock:
                Math.round((Number(row[53]) || 0) * 1000) / 1000,
              additional355mmTrenchBlock:
                Math.round((Number(row[54]) || 0) * 1000) / 1000,
              additional100mmBlockwork:
                Math.round((Number(row[55]) || 0) * 1000) / 1000,
              backfillExcavationsAsDug:
                Math.round((Number(row[56]) || 0) * 1000) / 1000,
              depositOnSite: Math.round((Number(row[57]) || 0) * 1000) / 1000,
              eoExposedBrickwork:
                Math.round((Number(row[58]) || 0) * 1000) / 1000,
              eoTanking:
                Math.round((Number(row[59]) || 0) * 1000) / 1000,
              depositCFAArisings:
                Math.round((Number(row[60]) || 0) * 1000) / 1000,
              pileCollar:
                Math.round((Number(row[61]) || 0) * 1000) / 1000,
              cordekCellcore155:
                Math.round((Number(row[62]) || 0) * 1000) / 1000,
              cordekCellcore225:
                Math.round((Number(row[63]) || 0) * 1000) / 1000,
              bbFloorHouse: Math.round((Number(row[65]) || 0) * 1000) / 1000,
              bbFloorGarage: Math.round((Number(row[66]) || 0) * 1000) / 1000,
              layOnlyFloors:
                Math.round((Number(row[67]) || 0) * 1000) / 1000,
              insituFloorHouse:
                Math.round((Number(row[68]) || 0) * 1000) / 1000,
              topHats:
                Math.round((Number(row[69]) || 0) * 1000) / 1000,
              eo2000gDPM:
                Math.round((Number(row[70]) || 0) * 1000) / 1000,
              eoAmber1DPM:
                Math.round((Number(row[71]) || 0) * 1000) / 1000,
              eoAmber2DPM:
                Math.round((Number(row[72]) || 0) * 1000) / 1000,
              radonMembrane:
                Math.round((Number(row[73]) || 0) * 1000) / 1000,
              gasMembrane:
                Math.round((Number(row[74]) || 0) * 1000) / 1000,
              meshToFloor:
                Math.round((Number(row[75]) || 0) * 1000) / 1000,
              topLayerMesh:
                Math.round((Number(row[76]) || 0) * 1000) / 1000,
              insituFloorGarage:
                Math.round((Number(row[77]) || 0) * 1000) / 1000,
              meshToGarageFloor:
                Math.round((Number(row[78]) || 0) * 1000) / 1000,
            });
          });

          // Keep rows that have both a valid plot number and a valid house type
          // (matches CutFills.tsx's import filter for consistency)
          const importedRows = allImportedRows.filter((row) => {
            const plotNoStr = String(row.plotNo || "").trim();
            const housetypeStr = String(row.housetype || "").trim();

            const hasValidPlotNo =
              plotNoStr !== "" &&
              plotNoStr !== "0" &&
              !plotNoStr.toLowerCase().includes("total") &&
              !plotNoStr.toLowerCase().includes("sum");
            const hasValidHouseType =
              housetypeStr !== "" &&
              !housetypeStr.toLowerCase().includes("total") &&
              !housetypeStr.toLowerCase().includes("sum") &&
              isNaN(Number(housetypeStr)) && // Not purely numeric
              housetypeStr.match(/[a-zA-Z]/); // Contains letters

            return hasValidPlotNo && hasValidHouseType;
          });

          if (importedRows.length === 0) {
            alert("No valid data found to import");
            return;
          }

          setData(importedRows);
          updateField("groundworks", {
            ...tender.groundworks,
            foundationsTable: importedRows,
            foundationsPrefixValues: extractedPrefixValues,
          } as any);
          alert(`Successfully imported ${importedRows.length} rows`);
        } catch (error) {
          console.error("Import error:", error);
          alert(`Import failed: ${error.message}`);
        }
      };

      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error("Import error:", error);
      alert(`Import failed: ${error.message}`);
    }
  };

  useEffect(() => {
    if (tender?.groundworks?.foundationsTable) {
      const tableData = tender.groundworks.foundationsTable as any[];
      // Check if data already has plotType assigned (from a recent import)
      const hasPlotType = tableData.some((row: any) => row.plotType === "plot" || row.plotType === "garage");
      // Check if data contains a "GARAGES" separator row (from old imports)
      const hasSeparator = tableData.some((row: any) => {
        const pn = String(row.plotNo || "").trim().toUpperCase();
        return pn === "GARAGES" || pn === "GARAGE";
      });

      if (hasPlotType && !hasSeparator) {
        // Data already has plotType and no separator to clean up — use as-is
        setData(tableData);
      } else {
        // Either old data without plotType, or has a GARAGES separator to remove
        let isGarageSection = false;
        let changed = false;
        const processedData: FoundationRow[] = [];
        tableData.forEach((row: any) => {
          const plotNoUpper = String(row.plotNo || "").trim().toUpperCase();
          if (plotNoUpper === "GARAGES" || plotNoUpper === "GARAGE") {
            isGarageSection = true;
            changed = true;
            return; // Skip the separator row
          }
          if (!row.plotType) changed = true;
          processedData.push({
            ...row,
            plotType: row.plotType || (isGarageSection ? "garage" : "plot"),
          });
        });
        setData(processedData);
        // Persist the cleaned-up data only when something actually changed.
        // Without this guard, an empty `foundationsTable` (no records) always
        // falls into this branch with `changed` never true, but `processedData`
        // is still a brand-new `[]` reference — persisting it unconditionally
        // fed a new array reference back into `tender.groundworks.foundationsTable`
        // every render, which is this effect's own dependency, causing an
        // infinite update loop that starved the UI (breadcrumb/stepper clicks
        // stopped navigating) whenever Foundations had no records.
        if (changed) {
          updateField("groundworks", {
            ...tender.groundworks,
            foundationsTable: processedData,
          });
        }
      }
    } else {
      // Initialize with empty row
      setData([createEmptyRow()]);
    }
  }, [tender?.groundworks?.foundationsTable]);

  const createEmptyRow = (key?: string, plotType: "plot" | "garage" = "plot"): FoundationRow => ({
    key: key || Date.now().toString(),
    plotNo: "",
    housetype: "",
    foundationType: "",
    plotType,
    pileMat: 0,
    pileMatRemoval: 0,
    plotGarSetOut: 0,
    pileSetOut: 0,
    pileAttendance: 0,
    excavateTrench: 0,
    excavatePiled: 0,
    excavateVibro: 0,
    levelCompact: 0,
    stockpileArisings: 0,
    concreteBlinding: 0,
    formplasOrSimilar: 0,
    cropPiles: 0,
    fillSteelPiles: 0,
    rebar: 0,
    stepsInFounds: 0,
    meshReinforcement: 0,
    gen1ConcreteToStrip: 0,
    rc35ConcreteToPiled: 0,
    rc35ConcreteToVibro: 0,
    stripClaymaster: 0,
    piledClaymaster: 0,
    // Substructure fields
    trench4NBlock300mmWide: 0,
    trench7NBlock300mmWide: 0,
    trench4NBlock355mmWide: 0,
    trench7NBlock355mmWide: 0,
    dense4NBlockwork100mm: 0,
    dense7NBlockwork100mm: 0,
    dense10NBlockwork100mm: 0,
    aerated4NBlockwork100mm: 0,
    aerated7NBlockwork100mm: 0,
    aerated4NBlockwork140mm: 0,
    aerated7NBlockwork140mm: 0,
    dense100mmCoursingBrick: 0,
    aerated100mmCoursingBrick: 0,
    aerated140mmCoursingBrick: 0,
    layFacingBrickwork: 0,
    supplyFacingBrickwork: 0,
    beamInfill: 0,
    formCavity: 0,
    cavityFill: 0,
    telescopicVents: 0,
    telescopicVents140: 0,
    perimeterInsulation: 0,
    perimeterInsulation30: 0,
    dpc100mm: 0,
    backfillExcavations: 0,
    cavityInsulation: 0,
    // Abnormals fields
    additionalExcavation: 0,
    gen1AdditionalConcrete: 0,
    additional300mmTrenchBlock: 0,
    additional355mmTrenchBlock: 0,
    additional100mmBlockwork: 0,
    backfillExcavationsAsDug: 0,
    depositOnSite: 0,
    eoExposedBrickwork: 0,
    eoTanking: 0,
    depositCFAArisings: 0,
    pileCollar: 0,
    cordekCellcore155: 0,
    cordekCellcore225: 0,
    // Floors fields
    bbFloorHouse: 0,
    bbFloorGarage: 0,
    layOnlyFloors: 0,
    insituFloorHouse: 0,
    topHats: 0,
    eo2000gDPM: 0,
    eoAmber1DPM: 0,
    eoAmber2DPM: 0,
    radonMembrane: 0,
    gasMembrane: 0,
    meshToFloor: 0,
    topLayerMesh: 0,
    insituFloorGarage: 0,
    meshToGarageFloor: 0,
  });

  // Mirrors the groundworksRef pattern used in CutFills.tsx/PlotDrives.tsx:
  // keeps a ref to the latest tender.groundworks so handleInputChange can
  // read it without closing over `tender` directly, letting handleInputChange
  // (and the column definitions that reference it) stay referentially stable
  // across renders for useMemo below, instead of rebuilding on every render.
  const groundworksRef = useRef(tender?.groundworks);
  useEffect(() => {
    groundworksRef.current = tender?.groundworks;
  }, [tender?.groundworks]);

  // Same reasoning as groundworksRef above: baseColumns (below) is memoized
  // on [handleInputChange] only, so a Plot No duplicate check inside its
  // render closure must read rows via a ref, not the `data` state directly
  // (which the closure would otherwise capture stale).
  const dataRef = useRef(data);
  useEffect(() => {
    dataRef.current = data;
  }, [data]);

  const handleInputChange = useCallback(
    (key: string, field: keyof FoundationRow, value: string | number) => {
      setData((prevData) => {
        const newData = prevData.map((row) => {
          if (row.key === key) {
            return { ...row, [field]: value };
          }
          return row;
        });

        // Update tender context
        updateField("groundworks", {
          ...groundworksRef.current,
          foundationsTable: newData,
        });
        return newData;
      });
    },
    [updateField],
  );

  const addRow = () => {
    const newRow = createEmptyRow(undefined, plotTypeFilter === "garages" ? "garage" : "plot");
    // trackRowAdded(newRow.key);
    const newData = [...data, newRow];
    setData(newData);
    updateField("groundworks", {
      ...tender.groundworks,
      foundationsTable: newData,
    });
  };

  const deleteRow = useCallback((key: string) => {
    setKeyToDelete(key);
    setKeysToDelete([]);
    setDeleteModalVisible(true);
  }, []);

  const deleteSelectedRows = () => {
    setKeysToDelete(selectedFoundationKeys as string[]);
    setKeyToDelete(null);
    setDeleteModalVisible(true);
  };

  const confirmDelete = () => {
    const keysBeingDeleted = keysToDelete.length > 0 ? keysToDelete : keyToDelete ? [keyToDelete] : [];
    if (keysBeingDeleted.length > 0) {
      // trackRowDeleted(keyToDelete);
      const keySet = new Set(keysBeingDeleted);
      const newData = data.filter((row) => !keySet.has(row.key));
      setData(newData);
      // Deleting the last remaining row also clears the ad-hoc extra items
      // grafted onto Foundations Bill Summary — see CutFills.tsx's identical
      // fix for the reasoning.
      updateField("groundworks", {
        ...tender.groundworks,
        foundationsTable: newData,
        ...(newData.length === 0 && { foundationsExtraItems: {} }),
      });
      setSelectedFoundationKeys([]);
    }
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  const cancelDelete = () => {
    setDeleteModalVisible(false);
    setKeyToDelete(null);
    setKeysToDelete([]);
  };

  // Filter data based on plotType dropdown. Memoized so this doesn't
  // re-filter the full table on every unrelated render (e.g. keystrokes in
  // other tabs' inputs) — only when the underlying rows or the view toggle
  // actually change (same fix applied to CutFills.tsx).
  const filteredData = React.useMemo(
    () =>
      data.filter((row) =>
        plotTypeFilter === "plots" ? row.plotType === "plot" || !row.plotType : row.plotType === "garage"
      ),
    [data, plotTypeFilter],
  );

  // Calculate totals for all numeric columns. Memoized off filteredData so
  // this reduce only re-runs when the visible rows change, not on every
  // render (same fix applied to CutFills.tsx).
  const totals = React.useMemo(() => {
    const totals = {
      pileMat: 0,
      pileMatRemoval: 0,
      plotGarSetOut: 0,
      pileSetOut: 0,
      pileAttendance: 0,
      excavateTrench: 0,
      excavatePiled: 0,
      excavateVibro: 0,
      levelCompact: 0,
      stockpileArisings: 0,
      concreteBlinding: 0,
      formplasOrSimilar: 0,
      cropPiles: 0,
      fillSteelPiles: 0,
      rebar: 0,
      stepsInFounds: 0,
      meshReinforcement: 0,
      gen1ConcreteToStrip: 0,
      rc35ConcreteToPiled: 0,
      rc35ConcreteToVibro: 0,
      stripClaymaster: 0,
      piledClaymaster: 0,
      trench4NBlock300mmWide: 0,
      trench7NBlock300mmWide: 0,
      trench4NBlock355mmWide: 0,
      trench7NBlock355mmWide: 0,
      dense4NBlockwork100mm: 0,
      dense7NBlockwork100mm: 0,
      dense10NBlockwork100mm: 0,
      aerated4NBlockwork100mm: 0,
      aerated7NBlockwork100mm: 0,
      aerated4NBlockwork140mm: 0,
      aerated7NBlockwork140mm: 0,
      dense100mmCoursingBrick: 0,
      aerated100mmCoursingBrick: 0,
      aerated140mmCoursingBrick: 0,
      layFacingBrickwork: 0,
      supplyFacingBrickwork: 0,
      beamInfill: 0,
      formCavity: 0,
      cavityFill: 0,
      telescopicVents: 0,
      telescopicVents140: 0,
      perimeterInsulation: 0,
      perimeterInsulation30: 0,
      dpc100mm: 0,
      backfillExcavations: 0,
      cavityInsulation: 0,
      additionalExcavation: 0,
      gen1AdditionalConcrete: 0,
      additional300mmTrenchBlock: 0,
      additional355mmTrenchBlock: 0,
      additional100mmBlockwork: 0,
      backfillExcavationsAsDug: 0,
      depositOnSite: 0,
      eoExposedBrickwork: 0,
      eoTanking: 0,
      depositCFAArisings: 0,
      pileCollar: 0,
      cordekCellcore155: 0,
      cordekCellcore225: 0,
      bbFloorHouse: 0,
      bbFloorGarage: 0,
      layOnlyFloors: 0,
      insituFloorHouse: 0,
      topHats: 0,
      eo2000gDPM: 0,
      eoAmber1DPM: 0,
      eoAmber2DPM: 0,
      radonMembrane: 0,
      gasMembrane: 0,
      meshToFloor: 0,
      topLayerMesh: 0,
      insituFloorGarage: 0,
      meshToGarageFloor: 0,
    };

    filteredData.forEach((row) => {
      Object.keys(totals).forEach((key) => {
        totals[key as keyof typeof totals] +=
          Math.round((Number(row[key as keyof FoundationRow]) || 0) * 1000) /
          1000;
      });
    });

    return totals;
  }, [filteredData]);

  const getTabDisplayName = (tabKey: string) => {
    const tabNames = {
      "excavate-concrete": "Excavate & Concrete",
      substructure: "Substructure",
      abnormals: "Abnormals",
      floors: "Floors",
    };
    return tabNames[tabKey as keyof typeof tabNames] || "Foundations";
  };

  // `sticky` renders the header in a separate DOM table from the body, so
  // `x: 'max-content'` (table-layout: auto) can't sync body column widths to
  // header text width — body cells stay at each column's explicit `width`
  // while the sticky header is sized independently, causing misalignment.
  // Passing the exact sum of column widths forces `table-layout: fixed` so
  // header and body cells share identical widths.
  const getScrollConfig = (totalColumnWidth: number) => {
    const estimatedRowHeight = 50;
    const maxComfortableRows = 12;
    const needsVerticalScroll = filteredData.length > maxComfortableRows;
    return { x: totalColumnWidth, y: needsVerticalScroll ? maxComfortableRows * estimatedRowHeight : undefined };
  };

  const handleSave = () => {
    saveTender();
    // Note: Don't reset changes here - changes persist until "Send for Review"
  };

  const handleSaveAndContinue = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and continue from Foundations", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/groundworks/drainage"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${error.response?.data?.message || error.message || "Unknown error"}`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  // Memoized so this array (and its per-render Input closures) is only
  // rebuilt when handleInputChange actually changes identity, instead of on
  // every keystroke/render (same fix applied to CutFills.tsx).
  const baseColumns = React.useMemo(
    () => [
    {
      title: "Plot No",
      dataIndex: "plotNo",
      key: "plotNo",
      width: 130,
      fixed: "left" as const,
      render: (text: string, record: FoundationRow) => {
        const prefix = record.plotType === "garage" ? "G-" : "P-";
        const suffix = text?.startsWith(prefix) ? text.slice(prefix.length) : (text ?? "");
        return (
          <BufferedTextInput
            addonBefore={prefix}
            value={suffix}
            onCommit={(newSuffix) => {
              const newPlotNo = prefix + newSuffix;
              if (isDuplicatePlotNo(dataRef.current, record.key, newPlotNo)) {
                message.error(`Plot No "${newPlotNo}" is already in use.`);
                return;
              }
              handleInputChange(record.key, "plotNo", newPlotNo);
            }}
            placeholder="1"
            style={{ minWidth: 80 }}
          />
        );
      },
    },
    {
      title: "Housetype",
      dataIndex: "housetype",
      key: "housetype",
      width: 110,
      fixed: "left" as const,
      render: (text: string, record: FoundationRow) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) =>
            handleInputChange(record.key, "housetype", newValue)
          }
          placeholder="Housetype"
        />
      ),
    },
    {
      title: "Foundation Type",
      dataIndex: "foundationType",
      key: "foundationType",
      width: 120,
      fixed: "left" as const,
      render: (text: string, record: FoundationRow) => (
        <BufferedTextInput
          value={text}
          onCommit={(newValue) =>
            handleInputChange(record.key, "foundationType", newValue)
          }
          placeholder="Foundation Type"
        />
      ),
    },
    ],
    [handleInputChange],
  );

  const excavateConcreteColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: formatColumnTitle(
          prefixValues.pileMat
            ? (() => {
                // LABEL-ONLY: convert a decimal stored value (e.g. "0.999")
                // to its integer-mm form ("999") before substituting. Raw
                // `prefixValues.pileMat` is untouched — rate calculations
                // continue to use the original value.
                const v = formatDimensionLabel(prefixValues.pileMat);
                return `${v.includes("mm") ? v : `${v}mm`} thick recycled 6F2 piling mat m²`;
              })()
            : "xxxmm thick recycled 6F2 piling mat m²",
        ),
        dataIndex: "pileMat",
        key: "pileMat",
        width: 200,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pileMat", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Excavate to remove pile mat & re-use m³"),
        dataIndex: "pileMatRemoval",
        key: "pileMatRemoval",
        width: 205,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pileMatRemoval", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Engineer setting out houses & garages Nr"),
        dataIndex: "plotGarSetOut",
        key: "plotGarSetOut",
        width: 205,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "plotGarSetOut", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Setting out for piling contractor Nr"),
        dataIndex: "pileSetOut",
        key: "pileSetOut",
        width: 195,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pileSetOut", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Attendance upon piling contractor Nr"),
        dataIndex: "pileAttendance",
        key: "pileAttendance",
        width: 195,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pileAttendance", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Excavate trench and tip on site m³"),
        dataIndex: "excavateTrench",
        key: "excavateTrench",
        width: 190,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "excavateTrench", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Excavate trench piled foundation and tip on site m³"),
        dataIndex: "excavatePiled",
        key: "excavatePiled",
        width: 235,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "excavatePiled", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Excavate vibro foundation and tip on site m³"),
        dataIndex: "excavateVibro",
        key: "excavateVibro",
        width: 215,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "excavateVibro", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Consolidate bottoms of trenches m²"),
        dataIndex: "levelCompact",
        key: "levelCompact",
        width: 190,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "levelCompact", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Deposit on site m³"),
        dataIndex: "stockpileArisings",
        key: "stockpileArisings",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "stockpileArisings", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          prefixValues.concreteBlinding
            ? (() => {
                // LABEL-ONLY: convert a decimal stored value (e.g. "0.999")
                // to its integer-mm form ("999") before substituting. Raw
                // `prefixValues.concreteBlinding` is untouched — rate
                // calculations continue to use the original value.
                const v = formatDimensionLabel(prefixValues.concreteBlinding);
                return `${v.includes("mm") ? v : `${v}mm`} thick concrete blinding m²`;
              })()
            : "xxmm thick concrete blinding m²",
        ),
        dataIndex: "concreteBlinding",
        key: "concreteBlinding",
        width: 185,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "concreteBlinding", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Formwork to groundbeams, (Formplas or similar) m"),
        dataIndex: "formplasOrSimilar",
        key: "formplasOrSimilar",
        width: 230,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "formplasOrSimilar", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Pile crop Nr"),
        dataIndex: "cropPiles",
        key: "cropPiles",
        width: 135,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "cropPiles", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Filling Steel Piles Nr"),
        dataIndex: "fillSteelPiles",
        key: "fillSteelPiles",
        width: 160,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "fillSteelPiles", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Steel Reinforcement (bar) t"),
        dataIndex: "rebar",
        key: "rebar",
        width: 175,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "rebar", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO step at party wall Nr"),
        dataIndex: "stepsInFounds",
        key: "stepsInFounds",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "stepsInFounds", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          meshReinforcementType
            ? `${meshReinforcementType} Mesh m²`
            : "Mesh Reinforcement m²"
        ),
        dataIndex: "meshReinforcement",
        key: "meshReinforcement",
        width: 180,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "meshReinforcement", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          getColumnTitle(
            "gen1ConcreteToStrip",
            "xx Concrete to Strip foundation (m³)"
          ) as string
        ),
        dataIndex: "gen1ConcreteToStrip",
        key: "gen1ConcreteToStrip",
        width: 205,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "gen1ConcreteToStrip", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          getColumnTitle(
            "rc35ConcreteToPiled",
            "xx Concrete to Piled foundation (m³)"
          ) as string
        ),
        dataIndex: "rc35ConcreteToPiled",
        key: "rc35ConcreteToPiled",
        width: 205,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "rc35ConcreteToPiled", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          getColumnTitle(
            "rc35ConcreteToVibro",
            "xx Concrete to Vibro foundation (m³)"
          ) as string
        ),
        dataIndex: "rc35ConcreteToVibro",
        key: "rc35ConcreteToVibro",
        width: 205,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "rc35ConcreteToVibro", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Strip Claymaster m²"),
        dataIndex: "stripClaymaster",
        key: "stripClaymaster",
        width: 155,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "stripClaymaster", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Piled Claymaster m²"),
        dataIndex: "piledClaymaster",
        key: "piledClaymaster",
        width: 155,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "piledClaymaster", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: FoundationRow) => (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, meshReinforcementType, prefixValues, formatColumnTitle]
  );

  // addColumnCodes() is a pure function but was previously invoked inline in
  // JSX on every render; memoizing its result means it only re-runs when the
  // underlying column definitions actually change (same fix applied to
  // CutFills.tsx/PlotDrives.tsx).
  const excavateConcreteColumnsWithCodes = React.useMemo(
    () => addColumnCodes(excavateConcreteColumns, effectiveFoundationsRefs),
    [excavateConcreteColumns, effectiveFoundationsRefs],
  );
  // getScrollConfig(...) previously ran inline in JSX on every render,
  // returning a brand-new object each time - memoized per tab so it stays
  // referentially stable across renders that don't touch filteredData.length.
  const excavateConcreteScrollConfig = React.useMemo(
    () => getScrollConfig(4605),
    [filteredData.length],
  );

  const substructureColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: getSubstructureColumnTitle("Trench block 300mm wide 3.6N m²"),
        dataIndex: "trench4NBlock300mmWide",
        key: "trench4NBlock300mmWide",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "trench4NBlock300mmWide", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Trench block 300mm wide 7.0N m²"),
        dataIndex: "trench7NBlock300mmWide",
        key: "trench7NBlock300mmWide",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "trench7NBlock300mmWide", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Trench block 355mm wide 3.6N m²"),
        dataIndex: "trench4NBlock355mmWide",
        key: "trench4NBlock355mmWide",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "trench4NBlock355mmWide", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Trench block 355mm wide 7.0N m²"),
        dataIndex: "trench7NBlock355mmWide",
        key: "trench7NBlock355mmWide",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "trench7NBlock355mmWide", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("3.6N 100mm Dense Blockwork m²"),
        dataIndex: "dense4NBlockwork100mm",
        key: "dense4NBlockwork100mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "dense4NBlockwork100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Dense concrete blockwork 100 thk 7n/mm2 (Ext Leaf) m²"),
        dataIndex: "dense7NBlockwork100mm",
        key: "dense7NBlockwork100mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "dense7NBlockwork100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("10N 100mm Dense Blockwork m²"),
        dataIndex: "dense10NBlockwork100mm",
        key: "dense10NBlockwork100mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "dense10NBlockwork100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated concrete blockwork 100 thk 3.6n/mm2 (Ext leaf) m²"),
        dataIndex: "aerated4NBlockwork100mm",
        key: "aerated4NBlockwork100mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "aerated4NBlockwork100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated concrete blockwork 100 thk 7n/mm2 (Ext leaf) m²"),
        dataIndex: "aerated7NBlockwork100mm",
        key: "aerated7NBlockwork100mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "aerated7NBlockwork100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated concrete blockwork 140 thk 3.6n/mm2 m²"),
        dataIndex: "aerated4NBlockwork140mm",
        key: "aerated4NBlockwork140mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "aerated4NBlockwork140mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated concrete blockwork 140 thk 7n/mm2 m²"),
        dataIndex: "aerated7NBlockwork140mm",
        key: "aerated7NBlockwork140mm",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "aerated7NBlockwork140mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Dense HB Common Brickwork - 100mm m²"),
        dataIndex: "dense100mmCoursingBrick",
        key: "dense100mmCoursingBrick",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "dense100mmCoursingBrick", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated HB Common Brickwork - 100mm m²"),
        dataIndex: "aerated100mmCoursingBrick",
        key: "aerated100mmCoursingBrick",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "aerated100mmCoursingBrick",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: getSubstructureColumnTitle("Aerated HB Common Brickwork - 140mm m²"),
        dataIndex: "aerated140mmCoursingBrick",
        key: "aerated140mmCoursingBrick",
        width: 120,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "aerated140mmCoursingBrick",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("HB wall in facings FREE ISSUE - 215 x 102.5 x 65mm m²"),
        dataIndex: "layFacingBrickwork",
        key: "layFacingBrickwork",
        width: 240,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "layFacingBrickwork", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("HB wall in Red Class B's (perf) M&J Supply - 215 x 102.5 x 65mm m²"),
        dataIndex: "supplyFacingBrickwork",
        key: "supplyFacingBrickwork",
        width: 200,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "supplyFacingBrickwork", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("T Closure (dense 100 wide) m"),
        dataIndex: "beamInfill",
        key: "beamInfill",
        width: 180,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "beamInfill", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Form Cavity m²"),
        dataIndex: "formCavity",
        key: "formCavity",
        width: 140,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "formCavity", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Cavity infill concrete m³"),
        dataIndex: "cavityFill",
        key: "cavityFill",
        width: 170,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "cavityFill", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Telescopic vents (including lintel) - 100 wide Nr"),
        dataIndex: "telescopicVents",
        key: "telescopicVents",
        width: 230,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "telescopicVents", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Telescopic vents (including lintel) - 140 wide Nr"),
        dataIndex: "telescopicVents140",
        key: "telescopicVents140",
        width: 230,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "telescopicVents140", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("25mm Perimeter Strip m"),
        dataIndex: "perimeterInsulation",
        key: "perimeterInsulation",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "perimeterInsulation", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("30mm Perimeter Strip m"),
        dataIndex: "perimeterInsulation30",
        key: "perimeterInsulation30",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "perimeterInsulation30", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("DPC 100mm wide m"),
        dataIndex: "dpc100mm",
        key: "dpc100mm",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "dpc100mm", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Backfill with as dug m³"),
        dataIndex: "backfillExcavations",
        key: "backfillExcavations",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "backfillExcavations", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Cavity insulation m"),
        dataIndex: "cavityInsulation",
        key: "cavityInsulation",
        width: 155,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "cavityInsulation", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: FoundationRow) => (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, substructureBlockType, getSubstructureColumnTitle, formatColumnTitle]
  );

  const substructureColumnsWithCodes = React.useMemo(
    () => addColumnCodes(substructureColumns, effectiveFoundationsRefs),
    [substructureColumns, effectiveFoundationsRefs],
  );
  const substructureScrollConfig = React.useMemo(
    () => getScrollConfig(4340),
    [filteredData.length],
  );

  const abnormalsColumns = React.useMemo(
    () => [
      ...baseColumns,
      {
        title: formatColumnTitle("Additional Excavation m³"),
        dataIndex: "additionalExcavation",
        key: "additionalExcavation",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "additionalExcavation", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle(
          getColumnTitle(
            "gen1AdditionalConcrete",
            "xx in additional concrete (m³)"
          ) as string
        ),
        dataIndex: "gen1AdditionalConcrete",
        key: "gen1AdditionalConcrete",
        width: 190,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "gen1AdditionalConcrete", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Additional 300mm Trench Block m²"),
        dataIndex: "additional300mmTrenchBlock",
        key: "additional300mmTrenchBlock",
        width: 185,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "additional300mmTrenchBlock",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Additional 355mm Trench Block m²"),
        dataIndex: "additional355mmTrenchBlock",
        key: "additional355mmTrenchBlock",
        width: 185,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "additional355mmTrenchBlock",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Additional 7.2N Aerated 100mm Blockwork m²"),
        dataIndex: "additional100mmBlockwork",
        key: "additional100mmBlockwork",
        width: 210,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "additional100mmBlockwork",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Backfill excavations with as dug m³"),
        dataIndex: "backfillExcavationsAsDug",
        key: "backfillExcavationsAsDug",
        width: 195,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(
                record.key,
                "backfillExcavationsAsDug",
                newValue
              )
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Deposit on site m³"),
        dataIndex: "depositOnSite",
        key: "depositOnSite",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "depositOnSite", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO exposed brickwork m²"),
        dataIndex: "eoExposedBrickwork",
        key: "eoExposedBrickwork",
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "eoExposedBrickwork", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO tanking, inc retaining blockwork m²"),
        dataIndex: "eoTanking",
        key: "eoTanking",
        width: 200,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "eoTanking", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Deposit CFA arisings on site m³"),
        dataIndex: "depositCFAArisings",
        key: "depositCFAArisings",
        width: 185,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "depositCFAArisings", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Pile Collar Nr"),
        dataIndex: "pileCollar",
        key: "pileCollar",
        width: 140,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "pileCollar", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Cordek Cellcore 155mm HX-B m²"),
        dataIndex: "cordekCellcore155",
        key: "cordekCellcore155",
        width: 180,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "cordekCellcore155", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Cordek Cellcore 225mm HX-B m²"),
        dataIndex: "cordekCellcore225",
        key: "cordekCellcore225",
        width: 180,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "cordekCellcore225", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: FoundationRow) => (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
    ],
    [baseColumns, formatColumnTitle, getColumnTitle]
  );

  const abnormalsColumnsWithCodes = React.useMemo(
    () => addColumnCodes(abnormalsColumns, effectiveFoundationsRefs),
    [abnormalsColumns, effectiveFoundationsRefs],
  );
  const abnormalsScrollConfig = React.useMemo(
    () => getScrollConfig(2800),
    [filteredData.length],
  );

  const floorsColumns = React.useMemo(
    () => {
      // The B&B Floor column and the Mesh column each occupy ONE table position
      // and swap their title / dataIndex / field key based on the page-level
      // "View" dropdown (plotTypeFilter): Plots → House / Mesh-to-Floor;
      // Garages → Garage / Mesh-to-Garage-Floor. `topLayerMesh` has no garage
      // counterpart and is hidden in Garages view.
      const isGarageView = plotTypeFilter === 'garages';
      const bbFieldKey = isGarageView ? 'bbFloorGarage' : 'bbFloorHouse';
      const bbPrefix = prefixValues[bbFieldKey];
      const bbTitle = bbPrefix && EVALUATE_CODES[bbPrefix as EvaluateCode]
        ? EVALUATE_CODES[bbPrefix as EvaluateCode].label + ' (m²)'
        : isGarageView
          ? 'B&B floor Garage m²'
          : 'B&B floor House m²';
      const meshFieldKey = isGarageView ? 'meshToGarageFloor' : 'meshToFloor';
      const meshPrefix = prefixValues[meshFieldKey];
      const meshTitle = isGarageView
        ? (meshPrefix ? `${meshPrefix} Mesh to Garage Floor m²` : 'Mesh to Garage floor m²')
        : (meshPrefix ? `${meshPrefix} Mesh to Floor m²` : 'Mesh to floor m²');

      const all = [
        ...baseColumns,
      {
        // Stable column `key` so antd reconciles this slot in place across
        // Plots ↔ Garages switches; the dynamic `dataIndex` (and title) are
        // what actually swap.
        title: formatColumnTitle(bbTitle),
        dataIndex: bbFieldKey,
        key: 'bbFloorMerged',
        width: bbPrefix ? 250 : 155,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, bbFieldKey, newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Lay only Floors m²"),
        dataIndex: "layOnlyFloors",
        key: "layOnlyFloors",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "layOnlyFloors", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Insitu Floor House m²"),
        dataIndex: "insituFloorHouse",
        key: "insituFloorHouse",
        width: 160,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "insituFloorHouse", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Top Hats"),
        dataIndex: "topHats",
        key: "topHats",
        width: 135,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "topHats", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO 2000g DPM m²"),
        dataIndex: "eo2000gDPM",
        key: "eo2000gDPM",
        width: 145,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "eo2000gDPM", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO Amber 1 DPM m²"),
        dataIndex: "eoAmber1DPM",
        key: "eoAmber1DPM",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "eoAmber1DPM", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("EO AMBER 2 DPM m²"),
        dataIndex: "eoAmber2DPM",
        key: "eoAmber2DPM",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "eoAmber2DPM", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Radon Membrane m²"),
        dataIndex: "radonMembrane",
        key: "radonMembrane",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "radonMembrane", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: formatColumnTitle("Gas Membrane with validation m²"),
        dataIndex: "gasMembrane",
        key: "gasMembrane",
        width: 185,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "gasMembrane", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        // Stable column key — see B&B Floor column above.
        title: formatColumnTitle(meshTitle),
        dataIndex: meshFieldKey,
        key: 'meshMerged',
        width: 165,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, meshFieldKey, newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      ...(isGarageView ? [] : [{
        title: formatColumnTitle(
          prefixValues['topLayerMesh'] ? `${prefixValues['topLayerMesh']} Top Layer Mesh m²` : "Top layer Mesh m²"
        ),
        dataIndex: "topLayerMesh",
        key: "topLayerMesh",
        width: 150,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "topLayerMesh", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      }]),
      {
        title: formatColumnTitle("Cast Insitu Slab - Garages m²"),
        dataIndex: "insituFloorGarage",
        key: "insituFloorGarage",
        width: 180,
        render: (value: number, record: FoundationRow) => (
          <NumberInput
            value={value || 0}
            onChange={(newValue) =>
              handleInputChange(record.key, "insituFloorGarage", newValue)
            }
            placeholder="0.000"
            decimalPlaces={3}
            style={{ textAlign: "right" }}
          />
        ),
      },
      {
        title: "Actions",
        key: "actions",
        width: 110,
        fixed: "right" as const,
        align: "center" as const,
        render: (_, record: FoundationRow) => (
          <Button
            type="text"
            danger
            icon={
              <DeleteOutlined style={{ color: "#ff4d4f", fontSize: "16px" }} />
            }
            onClick={() => deleteRow(record.key)}
            size="small"
          />
        ),
      },
      ];
      return all;
    },
    [baseColumns, formatColumnTitle, prefixValues, plotTypeFilter]
  );

  const floorsColumnsWithCodes = React.useMemo(
    () => addColumnCodes(floorsColumns, effectiveFoundationsRefs),
    [floorsColumns, effectiveFoundationsRefs],
  );
  const floorsScrollConfig = React.useMemo(
    () => getScrollConfig(2345),
    [filteredData.length],
  );
  const renderExcavateConcreteSummary = React.useCallback(() => {
                if (filteredData.length === 0) return null;
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{ backgroundColor: "#fff7e6", fontWeight: 600 }}
                    >
                      <Table.Summary.Cell index={0} colSpan={4} align="center">
                        <span style={{ color: "#000" }}>Totals</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.pileMat.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.pileMatRemoval.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.plotGarSetOut.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.pileSetOut.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.pileAttendance.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.excavateTrench.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.excavatePiled.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.excavateVibro.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.levelCompact.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.stockpileArisings.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={14} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.concreteBlinding.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={15} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.formplasOrSimilar.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={16} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.cropPiles.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={17} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.fillSteelPiles.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={18} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.rebar.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={19} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.stepsInFounds.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={20} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.meshReinforcement.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={21} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.gen1ConcreteToStrip.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={22} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.rc35ConcreteToPiled.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={23} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.rc35ConcreteToVibro.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={24} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.stripClaymaster.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={25} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.piledClaymaster.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={26} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
  }, [filteredData.length, totals]);

  const renderSubstructureSummary = React.useCallback(() => {
                if (filteredData.length === 0) return null;
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{ backgroundColor: "#fff7e6", fontWeight: 600 }}
                    >
                      <Table.Summary.Cell index={0} colSpan={4} align="center">
                        <span style={{ color: "#000" }}>Totals</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.trench4NBlock300mmWide.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.trench7NBlock300mmWide.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.trench4NBlock355mmWide.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.trench7NBlock355mmWide.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.dense4NBlockwork100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.dense7NBlockwork100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.dense10NBlockwork100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated4NBlockwork100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated7NBlockwork100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated4NBlockwork140mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={14} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated7NBlockwork140mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={15} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.dense100mmCoursingBrick.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={16} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated100mmCoursingBrick.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={17} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.aerated140mmCoursingBrick.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={18} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.layFacingBrickwork.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={19} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.supplyFacingBrickwork.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={20} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.beamInfill.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={21} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.formCavity.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={22} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.cavityFill.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={23} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.telescopicVents.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={24} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.telescopicVents140.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={25} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.perimeterInsulation.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={26} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.perimeterInsulation30.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={27} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.dpc100mm.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={28} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.backfillExcavations.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={29} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.cavityInsulation.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={30} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
  }, [filteredData.length, totals]);

  const renderAbnormalsSummary = React.useCallback(() => {
                if (filteredData.length === 0) return null;
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{ backgroundColor: "#fff7e6", fontWeight: "bold" }}
                    >
                      <Table.Summary.Cell index={0} colSpan={4} align="center">
                        <span style={{ color: "#000" }}>Totals</span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={4} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.additionalExcavation.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={5} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.gen1AdditionalConcrete.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={6} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.additional300mmTrenchBlock.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={7} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.additional355mmTrenchBlock.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={8} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.additional100mmBlockwork.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={9} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.backfillExcavationsAsDug.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={10} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.depositOnSite.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={11} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.eoExposedBrickwork.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={12} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.eoTanking.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={13} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.depositCFAArisings.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={14} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.pileCollar.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={15} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.cordekCellcore155.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={16} align="right">
                        <span
                          style={{
                            color: "#000",
                            textAlign: "right",
                            display: "block",
                            paddingRight: "8px",
                          }}
                        >
                          {totals.cordekCellcore225.toFixed(3)}
                        </span>
                      </Table.Summary.Cell>
                      <Table.Summary.Cell index={17} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
  }, [filteredData.length, totals]);

  const renderFloorsSummary = React.useCallback(() => {
                if (filteredData.length === 0) return null;
                // Build the summary in the same column order as `floorsColumns`
                // (which now collapses B&B-Floor / Mesh into a single Plots-vs-Garages
                // slot and hides `topLayerMesh` in Garages view). Without this the
                // summary held 17+ cells regardless of view, stretching the row
                // wider than the table on the Floors tab.
                const isGarageView = plotTypeFilter === 'garages';
                const cellStyle = {
                  color: "#000",
                  textAlign: "right" as const,
                  display: "block",
                  paddingRight: "8px",
                };
                const cells: { key: string; value: number }[] = [
                  { key: 'bb', value: isGarageView ? totals.bbFloorGarage : totals.bbFloorHouse },
                  { key: 'layOnlyFloors', value: totals.layOnlyFloors },
                  { key: 'insituFloorHouse', value: totals.insituFloorHouse },
                  { key: 'topHats', value: totals.topHats },
                  { key: 'eo2000gDPM', value: totals.eo2000gDPM },
                  { key: 'eoAmber1DPM', value: totals.eoAmber1DPM },
                  { key: 'eoAmber2DPM', value: totals.eoAmber2DPM },
                  { key: 'radonMembrane', value: totals.radonMembrane },
                  { key: 'gasMembrane', value: totals.gasMembrane },
                  { key: 'mesh', value: isGarageView ? totals.meshToGarageFloor : totals.meshToFloor },
                  ...(isGarageView ? [] : [{ key: 'topLayerMesh', value: totals.topLayerMesh }]),
                  { key: 'insituFloorGarage', value: totals.insituFloorGarage },
                ];
                return (
                  <Table.Summary fixed="bottom">
                    <Table.Summary.Row
                      style={{ backgroundColor: "#fff7e6", fontWeight: "bold" }}
                    >
                      <Table.Summary.Cell index={0} colSpan={4} align="center">
                        <span style={{ color: "#000" }}>Totals</span>
                      </Table.Summary.Cell>
                      {cells.map((cell, idx) => (
                        <Table.Summary.Cell key={cell.key} index={idx + 4} align="right">
                          <span style={cellStyle}>{cell.value.toFixed(3)}</span>
                        </Table.Summary.Cell>
                      ))}
                      {/* Empty cell aligning with the fixed-right "Actions" column */}
                      <Table.Summary.Cell index={cells.length + 4} />
                    </Table.Summary.Row>
                  </Table.Summary>
                );
  }, [filteredData.length, totals]);


  const handleSaveAndExit = async () => {
    try {
      message.loading({ content: "Saving tender...", key: "saveTender", duration: 0 });

      // Update tender state with hook values
      updateField("groundworks", {
        ...tender.groundworks,
        adjustments: {
          preliminaries: groundworksTotals.preliminariesAmount,
          ohp: groundworksTotals.ohpAmount,
          discount: groundworksTotals.discountAmount,
          includePreliminaries: groundworksTotals.preliminariesPercent > 0,
          includeOhp: groundworksTotals.ohpPercent > 0,
          includeDiscount: groundworksTotals.discountPercent > 0,
        },
        totals: {
          ...tender.groundworks?.totals,
          grandTotal: groundworksTotals.grandTotal,
          totalWithAdjustments: groundworksTotals.tenderTotal,
          tenderTotal: groundworksTotals.tenderTotal,
        },
      });

      updateField("globalSettings", {
        ...tender.globalSettings,
        groundworks: {
          overheadAndProfit: groundworksTotals.ohpPercent,
          preliminaries: groundworksTotals.preliminariesPercent,
          discount: groundworksTotals.discountPercent,
        },
      });

      updateField("status", "In-progress");
      updateField("lastModified", new Date());

      await saveTender();
      await new Promise((resolve) => setTimeout(resolve, 200));

      await forceSaveBothBoqEndpoints(tender, "Save and exit from Foundations", {
        grandTotal: groundworksTotals.grandTotal,
        grandTotalWithAdjustments: groundworksTotals.grandTotalWithAdjustments,
        preliminariesAmount: groundworksTotals.preliminariesAmount,
        ohpAmount: groundworksTotals.ohpAmount,
        discountAmount: groundworksTotals.discountAmount,
        tenderTotal: groundworksTotals.tenderTotal,
        preliminariesPercent: groundworksTotals.preliminariesPercent,
        ohpPercent: groundworksTotals.ohpPercent,
        discountPercent: groundworksTotals.discountPercent,
      }, user?.name, itemLibraryData);

      message.destroy("saveTender");
      setTimeout(() => navigate("/"), 500);
    } catch (error: any) {
      console.error("❌ Error submitting tender:", error);
      message.error({
        content: `Failed to submit tender: ${error.response?.data?.message || error.message || "Unknown error"}`,
        key: "saveTender",
        duration: 5,
      });
    }
  };

  return (
    <MainLayout
      title="Foundations"
      workflowType="groundworks"
      currentStep="foundations"
      previousStep="cut-fills"
      nextStep="cut-fill-sections"
      showFooter={false}
    >
      <Card
        className="shadow-sm foundations-card"
        bodyStyle={{ padding: 0 }}
        style={
          {
            borderRadius: "8px",
            overflow: "hidden",
            "--sticky-offset": `${stickyOffset}px`,
          } as React.CSSProperties
        }
      >
        {/* Header with tabs - Sticky together */}
        <div
          className="foundations-sticky-header"
          style={{
            position: "sticky",
            top: "144px",
            backgroundColor: "#fff",
            zIndex: 98,
            borderRadius: "8px 8px 0 0",
            isolation: "isolate",
          }}
        >
          {/* Header section */}
          <div
            className="px-6 pt-6 pb-4"
            style={{
              borderBottom: "1px solid #e5e7eb",
            }}
          >
            <div className="flex justify-between items-center">
              <div>
                <Title
                  level={3}
                  style={{
                    fontWeight: 600,
                    fontSize: "20px",
                    color: "#111827",
                    marginBottom: "8px",
                  }}
                >
                  Foundations
                </Title>
                <Text
                  type="secondary"
                  style={{ fontSize: "14px", color: "#6B7280" }}
                >
                  Enter foundation measurements for each plot across different
                  phases.
                </Text>
              </div>
              <Button
                icon={<SettingOutlined />}
                onClick={() => setConfigModalVisible(true)}
                type="default"
                className="configure-headers-btn"
                style={{
                  height: "32px",
                  borderRadius: "6px",
                  fontWeight: 500,
                  borderColor: "#1890ff",
                  color: "#1890ff",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  flexShrink: 0,
                  marginTop: "18px",
                }}
              >
                Configure Headers
              </Button>
            </div>
          </div>

          {/* Plot Type Filter Dropdown */}
          <div className="px-6" style={{ paddingTop: "16px", paddingBottom: "0px" }}>
            <div className="flex items-center gap-2">
              <Text style={{ fontWeight: 500 }}>View:</Text>
              <Select
                value={plotTypeFilter}
                onChange={setPlotTypeFilter}
                style={{ width: 220 }}
                size="middle"
                className="supplier-select"
              >
                <Option value="plots">Plots</Option>
                <Option value="garages">Garages</Option>
              </Select>
            </div>
          </div>

          {/* Tab Buttons - Part of sticky header */}
          <div
            className="foundations-sticky-tabs px-6"
            style={{
              paddingTop: "24px",
              paddingBottom: "0px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "4px",
              flexWrap: "wrap",
              backgroundColor: "#fff",
            }}
          >
            <div style={{ display: "flex", gap: "4px" }}>
              <Button
                type={activeTab === "excavate-concrete" ? "primary" : "default"}
                onClick={() => setActiveTab("excavate-concrete")}
                className={
                  activeTab === "excavate-concrete"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "excavate-concrete"
                      ? COLORS.primary
                      : "#E1E6EF",
                  borderColor:
                    activeTab === "excavate-concrete"
                      ? COLORS.primary
                      : "#E1E6EF",
                  color: activeTab === "excavate-concrete" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "excavate-concrete"
                      ? "none"
                      : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "excavate-concrete" ? 2 : 1,
                }}
              >
                Excavate & Concrete
              </Button>
              <Button
                type={activeTab === "substructure" ? "primary" : "default"}
                onClick={() => setActiveTab("substructure")}
                className={
                  activeTab === "substructure"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "substructure" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "substructure" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "substructure" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "substructure" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "substructure" ? 2 : 1,
                }}
              >
                Substructure
              </Button>
              <Button
                type={activeTab === "abnormals" ? "primary" : "default"}
                onClick={() => setActiveTab("abnormals")}
                className={
                  activeTab === "abnormals"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "abnormals" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "abnormals" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "abnormals" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "abnormals" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "abnormals" ? 2 : 1,
                }}
              >
                Abnormals
              </Button>
              <Button
                type={activeTab === "floors" ? "primary" : "default"}
                onClick={() => setActiveTab("floors")}
                className={
                  activeTab === "floors"
                    ? "active-tab-button"
                    : "inactive-tab-button"
                }
                style={{
                  backgroundColor:
                    activeTab === "floors" ? COLORS.primary : "#E1E6EF",
                  borderColor:
                    activeTab === "floors" ? COLORS.primary : "#E1E6EF",
                  color: activeTab === "floors" ? "#fff" : "#6B7280",
                  fontWeight: 500,
                  borderRadius: "6px 6px 0 0",
                  height: "38px",
                  paddingLeft: "20px",
                  paddingRight: "20px",
                  borderBottom:
                    activeTab === "floors" ? "none" : "1px solid #E1E6EF",
                  position: "relative",
                  zIndex: activeTab === "floors" ? 2 : 1,
                }}
              >
                Floors
              </Button>
            </div>

            {/* Configuration Button on the right - shown on every tab that has a Configure modal block */}
            <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
              {selectedFoundationKeys.length > 0 && (
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  onClick={deleteSelectedRows}
                  style={{
                    height: "32px",
                    borderRadius: "6px",
                    fontWeight: 500,
                    display: "flex",
                    alignItems: "center",
                    gap: "6px",
                  }}
                >
                  Delete Selected ({selectedFoundationKeys.length})
                </Button>
              )}
            </div>
          </div>

          {/* Dynamic Column Headers - moved to modal */}
        </div>

        {/* Content section - Tables */}
        <div
          className="px-6 pb-6 foundations-content-section"
          style={{ paddingTop: "0px", position: "relative", zIndex: 1 }}
        >
          {/* Excavate & Concrete Table - stays mounted once visited; toggled via
              CSS display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized FoundationsTableView so a plain tab
              switch (activeTab changing, nothing else) skips reconciling
              this table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("excavate-concrete") && (
          <div style={{ display: activeTab === "excavate-concrete" ? undefined : "none" }}>
            <FoundationsTableView
              isLoading={isExcavateConcreteTableLoading}
              dataSource={filteredData}
              columns={excavateConcreteColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={excavateConcreteScrollConfig}
              sticky={stickyConfig}
              renderSummary={renderExcavateConcreteSummary}
            />
          </div>
          )}

          {/* Substructure Table - stays mounted once visited; toggled via
              CSS display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized FoundationsTableView so a plain tab
              switch (activeTab changing, nothing else) skips reconciling
              this table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("substructure") && (
          <div style={{ display: activeTab === "substructure" ? undefined : "none" }}>
            <FoundationsTableView
              isLoading={isSubstructureTableLoading}
              dataSource={filteredData}
              columns={substructureColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={substructureScrollConfig}
              sticky={stickyConfig}
              renderSummary={renderSubstructureSummary}
            />
          </div>
          )}

          {/* Abnormals Table - stays mounted once visited; toggled via
              CSS display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized FoundationsTableView so a plain tab
              switch (activeTab changing, nothing else) skips reconciling
              this table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("abnormals") && (
          <div style={{ display: activeTab === "abnormals" ? undefined : "none" }}>
            <FoundationsTableView
              isLoading={isAbnormalsTableLoading}
              dataSource={filteredData}
              columns={abnormalsColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={abnormalsScrollConfig}
              sticky={stickyConfig}
              renderSummary={renderAbnormalsSummary}
            />
          </div>
          )}

          {/* Floors Table - stays mounted once visited; toggled via
              CSS display instead of unmount/remount (see mountedTabs above).
              Wrapped in the memoized FoundationsTableView so a plain tab
              switch (activeTab changing, nothing else) skips reconciling
              this table's rows entirely when it's the one being hidden. */}
          {mountedTabs.has("floors") && (
          <div style={{ display: activeTab === "floors" ? undefined : "none" }}>
            <FoundationsTableView
              isLoading={isFloorsTableLoading}
              dataSource={filteredData}
              columns={floorsColumnsWithCodes}
              rowSelection={rowSelectionConfig}
              scroll={floorsScrollConfig}
              sticky={stickyConfig}
              renderSummary={renderFloorsSummary}
            />
          </div>
          )}

          {/* Add New Row Button */}
          <div style={{ marginTop: "16px" }}>
            <Button
              type="text"
              icon={<PlusOutlined style={{ color: COLORS.primary }} />}
              onClick={addRow}
              style={{
                color: COLORS.primary,
                fontWeight: 500,
                padding: "4px 8px",
              }}
            >
              Add New Row
            </Button>
          </div>
        </div>

        {/* Footer with action buttons */}
        <div style={{ borderTop: "1px solid #e5e7eb" }}>
          <div className="px-6 py-4 flex justify-end gap-3">
            <Button
              onClick={handleSaveAndExit}
              style={{
                borderColor: COLORS.primary,
                color: COLORS.primary,
              }}
            >
              Save and Exit
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndContinue}
              style={{
                backgroundColor: COLORS.primary,
                borderColor: COLORS.primary,
              }}
            >
              Save & Continue <ArrowRightOutlined />
            </Button>
          </div>
        </div>

        {/* Excel Import Modal - Hidden, only for modal functionality */}
        <div style={{ display: "none" }}>
          <ExcelImportExport
            onImport={handleImportExcel}
            onDownloadTemplate={handleDownloadTemplate}
            importLoading={importLoading}
            importModalVisible={importModalVisible}
            setImportModalVisible={setImportModalVisible}
            hasData={data.length > 0}
          />
        </div>
      </Card>

      <style>{`
        .foundations-card.ant-card {
          border-radius: 8px !important;
          overflow: visible !important;
        }

        .foundations-card.ant-card .ant-card-body {
          border-radius: 8px !important;
          overflow: visible !important;
          position: relative !important;
        }

        .foundations-sticky-header {
          box-shadow: none;
          will-change: transform;
          transform: translateZ(0);
          z-index: 98 !important;
        }

        .foundations-sticky-tabs {
          border-bottom: none;
          min-height: 40px;
        }

        /* Ensure MainLayout doesn't interfere with positioning */
        .foundations-card {
          position: relative;
          height: auto;
          overflow: visible !important;
        }

        .foundations-card .ant-card-body {
          overflow: visible !important;
        }

        .foundations-content-section {
          clip-path: inset(0px -100vw -100vh -100vw);
        }

        /* Ensure table wrapper allows horizontal scroll with sticky headers */
        .foundations-table .ant-table-body {
          overflow-x: auto !important;
          margin-top: 0 !important;
        }

        .active-tab-button {
          border-bottom: none !important;
        }

        .inactive-tab-button {
          border-bottom: none !important;
        }

        .foundations-table {
          font-size: 14px;
          margin-top: 0 !important;
        }

        .foundations-table .ant-table {
          border: none !important;
          margin-top: 0 !important;
        }

        .foundations-table .ant-table-container {
          border: 1px solid #e5e7eb !important;
          border-top: none !important;
          border-radius: 0 !important;
        }

        .foundations-table .ant-table-thead > tr > th {
          min-width: 110px !important;
          background-color: ${COLORS.tableHeaderBg} !important;
          font-weight: 600 !important;
          color: #000 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          padding: 16px 8px 10px 8px !important;
          text-align: center !important;
          font-size: 13px !important;
          position: sticky !important;
          top: var(--sticky-offset, 310px) !important;
          will-change: transform !important;
          transform: translateZ(0) !important;
          z-index: 97 !important;
        }

        .foundations-table .ant-table-thead > tr > th:first-child {
          border-left: none !important;
        }

        .foundations-table .ant-table-thead > tr > th.ant-table-cell-fix-left {
          z-index: 99 !important;
        }

        .foundations-table .ant-table-tbody > tr > td {
          min-width: 110px !important;
          padding: 6px 8px !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: 1px solid #e5e7eb !important;
          border-left: none !important;
          border-top: none !important;
          background-color: #fff;
        }

        .foundations-table .ant-table-tbody > tr > td:first-child {
          border-left: none !important;
        }

        .foundations-table .ant-table-tbody > tr:hover > td {
          background-color: #fafafa !important;
        }

        .foundations-table .ant-table-tbody > tr.ant-table-row-selected > td {
          background-color: #fff !important;
        }

        .foundations-table .ant-input {
          border: 1px solid #d9d9d9 !important;
          border-radius: 4px !important;
          padding: 4px 11px !important;
          font-size: 14px !important;
          height: 32px !important;
        }

        /* All columns are NumberInput components, so right-align all text inputs */
        .foundations-table .ant-input[type="text"] {
          text-align: right !important;
        }

        .foundations-table .ant-input[type="number"] {
          text-align: right !important;
        }

        .foundations-table .ant-input:focus {
          border-color: ${COLORS.primary} !important;
          box-shadow: 0 0 0 2px rgba(22, 126, 249, 0.1) !important;
        }

        .foundations-table .ant-input-number {
          width: 100%;
        }

        .foundations-table .ant-table-summary > tr > td {
          background-color: #FFF7E6 !important;
          border-right: 1px solid #e5e7eb !important;
          border-bottom: none !important;
          border-left: none !important;
          border-top: none !important;
          padding: 10px 8px !important;
          font-weight: 600 !important;
        }

        .foundations-table .ant-table-summary > tr > td:first-child {
          border-left: none !important;
        }

        .foundations-table .ant-btn-text.ant-btn-dangerous {
          color: #ff4d4f !important;
        }

        .foundations-table .ant-btn-text.ant-btn-dangerous:hover {
          background-color: #fff1f0 !important;
        }
      `}</style>

      {/* Foundation Custom Configuration Modal */}
      <ColumnConfigModal
        title="Configure Headers"
        open={configModalVisible}
        onClose={() => {
          // Reset temp values back to actual values when modal closes without applying
          setTempMeshReinforcementType(meshReinforcementType);
          setTempSubstructureBlockType(substructureBlockType);
          setTempPrefixValues({ ...prefixValues });
          setConfigModalVisible(false);
        }}
        onApply={() => {
          // Apply temporary changes to actual state
          setMeshReinforcementType(tempMeshReinforcementType);
          setSubstructureBlockType(tempSubstructureBlockType);
          setPrefixValues(tempPrefixValues);
          // Persist all three changes in a single dispatch so the per-field save
          // effects don't race and overwrite each other with stale spreads.
          updateField("groundworks", {
            ...tender.groundworks,
            meshReinforcementType: tempMeshReinforcementType,
            substructureBlockType: tempSubstructureBlockType,
            foundationsPrefixValues: tempPrefixValues,
          } as any);
          // Close the modal after applying changes
          setConfigModalVisible(false);
        }}
        width={activeTab === "floors" ? 600 : activeTab === "substructure" ? 500 : 700}
        isViewMode={tender.isViewMode}
      >
        {/* Floors Tab Content */}
        {activeTab === "floors" ? (
          <div>
            {/* Field set follows the page-level "View" dropdown (plotTypeFilter):
                "plots" → B&B Floor House + Mesh to Floor + Top Layer Mesh;
                "garages" → B&B Floor Garage + Mesh to Garage Floor. */}
            {plotTypeFilter !== "garages" ? (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 24, rowGap: 20 }}>
                {/* B&B Floor House */}
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
                    B&B Floor House
                  </Text>
                  <Text className="text-xs mb-2 block">Select B&B Floor Type</Text>
                  <select
                    value={tempPrefixValues["bbFloorHouse"] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setTempPrefixValues((prev) => {
                          const newValues = { ...prev };
                          delete newValues["bbFloorHouse"];
                          return newValues;
                        });
                      } else {
                        setTempPrefixValues((prev) => ({ ...prev, bbFloorHouse: value }));
                      }
                    }}
                    style={selectStyles}
                  >
                    <option value="">-- Select --</option>
                    <option value="GWALPF005">Concrete beam &amp; block floor; 225mm Thermal Economics Insulation - B&amp;B houses</option>
                    <option value="GWALPF007">Concrete beam &amp; polystyrene infill-panel floor (0.15 U-value) - B&amp;B houses</option>
                    <option value="GWALPF008">Concrete beam &amp; polystyrene infill-panel floor (0.13 U-value) - B&amp;B houses</option>
                    <option value="GWALPF009">Concrete beam &amp; polystyrene infill-panel floor (0.11 U-value) - B&amp;B houses</option>
                    <option value="GWALPF010">Concrete beam &amp; block floor; 175mm Jablite Insulation - B&amp;B houses</option>
                    <option value="GWALPF014">Composite Jetfloor, 150mm beams - B&amp;B houses - Countryside Homes</option>
                    <option value="GWALPF015">Composite Jetfloor, 150mm beams - B&amp;B houses - Redrow Homes</option>
                    <option value="GWALPF021">Composite Jetfloor, 150mm beams - B&amp;B houses - Miller Homes</option>
                    <option value="GWALPF017">McCann 150mm beams and block floor - B&amp;B houses - Vistry Homes</option>
                    <option value="GWALPF018">Longley 150mm beams and block floor - B&amp;B houses - Avant, Morris Homes</option>
                    <option value="GWALPF019">Litecast XT 150mm beams and block floor - B&amp;B houses - St Modwen</option>
                    <option value="GWALPF020">Jablite all in one thermal floor - B&amp;B houses - Davidsons Homes</option>
                  </select>
                </div>

                {/* Mesh to Floor */}
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
                    Mesh to Floor
                  </Text>
                  <Text className="text-xs mb-2 block">Select Mesh Type</Text>
                  <select
                    value={tempPrefixValues['meshToFloor'] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setTempPrefixValues((prev) => {
                          const next = { ...prev };
                          delete next['meshToFloor'];
                          return next;
                        });
                      } else {
                        setTempPrefixValues((prev) => ({ ...prev, meshToFloor: value }));
                      }
                    }}
                    style={selectStyles}
                  >
                    <option value="">-- Select --</option>
                    <option value="A142">A142</option>
                    <option value="A193">A193</option>
                    <option value="A252">A252</option>
                    <option value="A393">A393</option>
                    <option value="B283">B283</option>
                    <option value="B385">B385</option>
                    <option value="B503">B503</option>
                    <option value="B785">B785</option>
                    <option value="B1131">B1131</option>
                  </select>
                </div>

                {/* Top Layer Mesh */}
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
                    Top Layer Mesh
                  </Text>
                  <Text className="text-xs mb-2 block">Select Mesh Type</Text>
                  <select
                    value={tempPrefixValues['topLayerMesh'] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setTempPrefixValues((prev) => {
                          const next = { ...prev };
                          delete next['topLayerMesh'];
                          return next;
                        });
                      } else {
                        setTempPrefixValues((prev) => ({ ...prev, topLayerMesh: value }));
                      }
                    }}
                    style={selectStyles}
                  >
                    <option value="">-- Select --</option>
                    <option value="A142">A142</option>
                    <option value="A193">A193</option>
                    <option value="A252">A252</option>
                    <option value="A393">A393</option>
                    <option value="B283">B283</option>
                    <option value="B385">B385</option>
                    <option value="B503">B503</option>
                    <option value="B785">B785</option>
                  </select>
                </div>
              </div>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", columnGap: 24, rowGap: 20 }}>
                {/* B&B Floor Garage */}
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
                    B&B Floor Garage
                  </Text>
                  <Text className="text-xs mb-2 block">Select Garage Floor Type</Text>
                  <select
                    value={tempPrefixValues["bbFloorGarage"] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setTempPrefixValues((prev) => {
                          const next = { ...prev };
                          delete next["bbFloorGarage"];
                          return next;
                        });
                      } else {
                        setTempPrefixValues((prev) => ({ ...prev, bbFloorGarage: value }));
                      }
                    }}
                    style={selectStyles}
                  >
                    <option value="">-- Select --</option>
                    <option value="GWALPF006">Garage - Beam and block</option>
                    <option value="GWALPF012">Cast Insitu Slab</option>
                  </select>
                </div>

                {/* Mesh to Garage Floor */}
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
                    Mesh to Garage Floor
                  </Text>
                  <Text className="text-xs mb-2 block">Select Mesh Type</Text>
                  <select
                    value={tempPrefixValues['meshToGarageFloor'] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!value) {
                        setTempPrefixValues((prev) => {
                          const next = { ...prev };
                          delete next['meshToGarageFloor'];
                          return next;
                        });
                      } else {
                        setTempPrefixValues((prev) => ({ ...prev, meshToGarageFloor: value }));
                      }
                    }}
                    style={selectStyles}
                  >
                    <option value="">-- Select --</option>
                    <option value="A142">A142</option>
                    <option value="A193">A193</option>
                    <option value="A252">A252</option>
                    <option value="A393">A393</option>
                    <option value="B283">B283</option>
                    <option value="B385">B385</option>
                    <option value="B503">B503</option>
                    <option value="B785">B785</option>
                    <option value="B1131">B1131</option>
                  </select>
                </div>
              </div>
            )}
          </div>
        ) : /* Substructure Tab Content */
        activeTab === "substructure" ? (
          <div>
            <Text strong className="text-xs text-gray-600 uppercase mb-3 block">
              Block Type Selection
            </Text>
            <div>
              <Text className="text-xs mb-2 block">Select Block Type</Text>
              <select
                value={tempSubstructureBlockType}
                onChange={(e) => setTempSubstructureBlockType(e.target.value)}
                style={selectStyles}
              >
                <option value="">Select</option>
                <option value="Thermalite">Thermalite</option>
                <option value="Toplite">Toplite</option>
                <option value="Celcon - general">Celcon - general</option>
                <option value="Celcon - Barratt / Redrow deal">
                  Celcon - Barratt / Redrow deal
                </option>
              </select>
            </div>
          </div>
        ) : (
          <>
            {/* Excavation Values — temporarily hidden
            <div className="mb-4">
              <Text strong className="text-xs text-gray-600 uppercase">
                Excavation Values
              </Text>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Text className="text-xs mb-1 block">Exc Trench</Text>
                  <NumberInput
                    value={tempPrefixValues.excTrench || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        excTrench: value,
                      }))
                    }
                    disabled
                  />
                </div>
                <div>
                  <Text className="text-xs mb-1 block">Exc Piled</Text>
                  <NumberInput
                    value={tempPrefixValues.excPiled || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        excPiled: value,
                      }))
                    }
                    disabled
                  />
                </div>
                <div>
                  <Text className="text-xs mb-1 block">Exc Vibro</Text>
                  <NumberInput
                    value={tempPrefixValues.excVibro || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        excVibro: value,
                      }))
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
            <div style={{ borderTop: "1px solid #e8e8e8", margin: "16px 0" }} />
            */}

            {/* Concrete Values — temporarily hidden
            <div className="mb-4">
              <Text strong className="text-xs text-gray-600 uppercase">
                Concrete Values
              </Text>
              <div className="grid grid-cols-3 gap-3 mt-2">
                <div>
                  <Text className="text-xs mb-1 block">Conc Trench</Text>
                  <NumberInput
                    value={tempPrefixValues.concTrench || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        concTrench: value,
                      }))
                    }
                    disabled
                  />
                </div>
                <div>
                  <Text className="text-xs mb-1 block">Conc Piled</Text>
                  <NumberInput
                    value={tempPrefixValues.concPiled || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        concPiled: value,
                      }))
                    }
                    disabled
                  />
                </div>
                <div>
                  <Text className="text-xs mb-1 block">Conc Vibro</Text>
                  <NumberInput
                    value={tempPrefixValues.concVibro || 0}
                    onChange={(value) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        concVibro: value,
                      }))
                    }
                    disabled
                  />
                </div>
              </div>
            </div>
            <div style={{ borderTop: "1px solid #e8e8e8", margin: "16px 0" }} />
            */}

            {/* Excavate & Concrete tab content (Piling Mat / Concrete Blinding /
                Terram / Foundation Types). Hidden on the Abnormals tab — only
                the Abnormals "In Additional Concrete" block below is shown there. */}
            {activeTab === "excavate-concrete" && (
            <>
            {/* Piling Mat & Concrete Blinding — side by side */}
            <div className="mb-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase">
                    Piling Mat
                  </Text>
                  <div className="mt-2">
                    <Text className="text-xs mb-1 block">xxxmm thick recycled 6F2 piling mat</Text>
                    <NumberInput
                      value={(() => {
                        const raw = parseFloat(tempPrefixValues.pileMat) || 0;
                        return raw >= 1 ? raw / 1000 : raw;
                      })()}
                      onChange={(value) => {
                        const metresValue = value ? parseFloat(value.toString()) : 0;
                        if (!metresValue) {
                          setTempPrefixValues((prev) => {
                            const newValues = { ...prev };
                            delete newValues.pileMat;
                            return newValues;
                          });
                        } else {
                          const mmValue = String(Math.round(metresValue * 1000));
                          setTempPrefixValues((prev) => ({
                            ...prev,
                            pileMat: mmValue,
                          }));
                        }
                      }}
                      positiveOnly
                      decimalPlaces={3}
                      zeroIntegerOnly
                    />
                  </div>
                </div>
                <div>
                  <Text strong className="text-xs text-gray-600 uppercase">
                    Concrete Blinding
                  </Text>
                  <div className="mt-2">
                    <Text className="text-xs mb-1 block">xxmm thick concrete blinding</Text>
                    <NumberInput
                      value={(() => {
                        const raw = parseFloat(tempPrefixValues.concreteBlinding) || 0;
                        return raw >= 1 ? raw / 1000 : raw;
                      })()}
                      onChange={(value) => {
                        const metresValue = value ? parseFloat(value.toString()) : 0;
                        if (!metresValue) {
                          setTempPrefixValues((prev) => {
                            const newValues = { ...prev };
                            delete newValues.concreteBlinding;
                            return newValues;
                          });
                        } else {
                          const mmValue = String(Math.round(metresValue * 1000));
                          setTempPrefixValues((prev) => ({
                            ...prev,
                            concreteBlinding: mmValue,
                          }));
                        }
                      }}
                      positiveOnly
                      decimalPlaces={3}
                      zeroIntegerOnly
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Terram — adds ALP031A to the Piling Mat resource code (combined as ALP031A+GWALP004). */}
            <div className="mb-4">
              <Checkbox
                checked={tempPrefixValues.terram === 'true'}
                onChange={(e) => {
                  const checked = e.target.checked;
                  setTempPrefixValues((prev) => {
                    const next = { ...prev };
                    if (checked) {
                      next.terram = 'true';
                    } else {
                      delete next.terram;
                    }
                    return next;
                  });
                }}
              >
                <Text className="text-xs">
                  Terram 
                  {/* <Text type="secondary" className="text-xs">(adds ALP031A to Piling Mat — combined code: ALP031A+GWALP004)</Text> */}
                </Text>
              </Checkbox>
            </div>

            <div style={{ borderTop: "1px solid #e8e8e8", margin: "16px 0" }} />

            {/* Foundation Types Section */}
            <div>
              <Text strong className="text-xs text-gray-600 uppercase">
                Foundation Types
              </Text>
              <div className="grid grid-cols-2 gap-3 mt-2">
                <div>
                  <Text className="text-xs mb-1 block">Mesh Reinforcement</Text>
                  <select
                    value={tempMeshReinforcementType}
                    onChange={(e) => setTempMeshReinforcementType(e.target.value)}
                    style={selectStyles}
                  >
                    <option value="">Select</option>
                    <option value="A142">A142</option>
                    <option value="A193">A193</option>
                    <option value="A252">A252</option>
                    <option value="A393">A393</option>
                    <option value="B283">B283</option>
                    <option value="B385">B385</option>
                    <option value="B503">B503</option>
                    <option value="B785">B785</option>
                    <option value="B1131">B1131</option>
                  </select>
                </div>

                <div>
                  <Text className="text-xs mb-1 block">Concrete to Strip</Text>
                  <select
                    value={tempPrefixValues.gen1ConcreteToStrip || ""}
                    onChange={(e) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        gen1ConcreteToStrip: e.target.value,
                      }))
                    }
                    style={selectStyles}
                  >
                    <option value="">Select</option>
                    <option value="GEN1">GEN1</option>
                    <option value="GEN2">GEN2</option>
                    <option value="GEN3">GEN3</option>
                    <option value="FND2">FND2</option>
                    <option value="FND3z">FND3z</option>
                    <option value="FND4">FND4</option>
                    <option value="RC28/35">RC28/35</option>
                    <option value="RC32/40">RC32/40</option>
                    <option value="C16/20">C16/20</option>
                  </select>
                </div>

                <div>
                  <Text className="text-xs mb-1 block">Concrete to Piled</Text>
                  <select
                    value={tempPrefixValues.rc35ConcreteToPiled || ""}
                    onChange={(e) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        rc35ConcreteToPiled: e.target.value,
                      }))
                    }
                    style={selectStyles}
                  >
                    <option value="">Select</option>
                    <option value="RC28/35">RC28/35</option>
                    <option value="RC32/40">RC32/40</option>
                    <option value="C16/20">C16/20</option>
                  </select>
                </div>

                <div>
                  <Text className="text-xs mb-1 block">Concrete to Vibro</Text>
                  <select
                    value={tempPrefixValues.rc35ConcreteToVibro || ""}
                    onChange={(e) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        rc35ConcreteToVibro: e.target.value,
                      }))
                    }
                    style={selectStyles}
                  >
                    <option value="">Select</option>
                    <option value="RC28/35">RC28/35</option>
                    <option value="RC32/40">RC32/40</option>
                    <option value="C16/20">C16/20</option>
                  </select>
                </div>
              </div>
            </div>
            </>
            )}

            {/* Abnormals-only: "in additional concrete" — same dropdown as
                Concrete to Strip; resource code becomes GWALP013+RMC<xxx>. */}
            {activeTab === "abnormals" && (
              <div>
                <Text strong className="text-xs text-gray-600 uppercase">
                  Abnormals
                </Text>
                <div className="mt-2">
                  <Text className="text-xs mb-1 block">In Additional Concrete</Text>
                  <select
                    value={tempPrefixValues.gen1AdditionalConcrete || ""}
                    onChange={(e) =>
                      setTempPrefixValues((prev) => ({
                        ...prev,
                        gen1AdditionalConcrete: e.target.value,
                      }))
                    }
                    style={{ ...selectStyles, width: "100%" }}
                  >
                    <option value="">Select</option>
                    <option value="GEN1">GEN1</option>
                    <option value="GEN2">GEN2</option>
                    <option value="GEN3">GEN3</option>
                    <option value="FND2">FND2</option>
                    <option value="FND3z">FND3z</option>
                    <option value="FND4">FND4</option>
                    <option value="RC28/35">RC28/35</option>
                    <option value="RC32/40">RC32/40</option>
                    <option value="C16/20">C16/20</option>
                  </select>
                </div>
              </div>
            )}
          </>
        )}
      </ColumnConfigModal>

      <DeleteConfirmationModal
        visible={deleteModalVisible}
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        title={keysToDelete.length > 1 ? `Are you sure you want to delete ${keysToDelete.length} records?` : undefined}
      />
    </MainLayout>
  );
};

export default FoundationsNew;
